// src/anamneses.js

export const ANAMNESE_DATA = {
"Ginecologia e Obstetrícia": {
  first: `Anamnese para Primeira Consulta - Ginecologia e Obstetrícia

## 1. Identificação
**Nome:**
**Idade:**
**Estado Civil:**
**Endereço:**
**Escolaridade:**
**Procedência:**
**Raça/Cor:**
**Profissão:**
**Data da Consulta:**

## 2. Queixa Principal (QP)
**Motivo principal da consulta:**
> (Descrever com as palavras da paciente, por exemplo: "Retorno após laqueadura", "Dor pélvica há 2 meses").

## 3. História da Doença Atual (HDA)
**Início dos sintomas:**
* Quando começaram? (Data ou estimativa)
* Foi súbito ou gradual?

**Características dos sintomas:**
* Localização, intensidade (escala de 0 a 10), duração.
* Irradiação ou alteração com o ciclo menstrual (se aplicável).

**Evolução dos sintomas:**
* Os sintomas melhoraram, pioraram ou permaneceram os mesmos desde o início?

**Fatores que pioram ou aliviam:**
* (medicamentos, posição, jejum, alimentação, etc.)

**Sintomas associados:**
* Presença de febre, corrimentos, sangramentos, dor abdominal ou distensão.

**Tratamentos prévios:**
* Medicamentos ou intervenções já realizadas.

## 4. Antecedentes Pessoais Patológicos (APP)
**Alergias:**
* Medicamentos, alimentos ou substâncias químicas.

**Medicações em uso:**
* Nome, dose, frequência e indicação.

**Histórico cirúrgico:**
* Tipo de cirurgia, motivo e data.

**Histórico clínico:**
* Doenças crônicas (diabetes, hipertensão, dislipidemia, doenças autoimunes, etc.).

**Outros antecedentes:**
* Traumas, transfusões de sangue, infecções de repetição.

**Hábitos de vida:**
* Tabagismo, etilismo, atividade física e alimentação.

## 5. Revisão de Sistemas
**Geral:**
* Febre, fadiga, perda ou ganho de peso.

**Gastrointestinal:**
* Dor abdominal, alterações no hábito intestinal (frequência, consistência, cor).

**Urinário:**
* Disúria, frequência urinária aumentada, incontinência.

**Neurológico:**
* Cefaleia, tonturas, alterações de sensibilidade.

**Cardiovascular:**
* Edema, palpitações, dor torácica.

## 6. Antecedentes Ginecológicos
**Ciclo Menstrual:**
* Menarca: Idade do início da menstruação.
* DUM: Data da última menstruação.
* Intervalo e duração: Frequência e quantidade (hipermenorreia, hipomenorreia, etc.).

**Histórico sexual:**
* Sexarca: Idade do início da vida sexual.
* Número de parceiros: Atual e passados.
* Método contraceptivo: Atual e anteriores.

**Alterações ginecológicas:**
* Corrimento vaginal, coceira, dor durante a relação sexual (dispareunia), diminuição da libido.

**Cirurgias ginecológicas:**
* Laqueadura, cesárea, histerectomia, entre outras.

**Alterações hormonais:**
* Sintomas de menopausa (fogachos, insônia, irritabilidade).

## 7. Antecedentes Obstétricos
**Gestas, Partos, Abortos e Nascidos Vivos:**
* GxPxAxNx (Gestas, Partos, Abortos, Nascidos Vivos).

**Histórico de complicações gestacionais:**
* Pré-eclâmpsia, diabetes gestacional, prematuridade.

## 8. Exame Físico
**Sinais Vitais:**
* Pressão arterial:
* Frequência cardíaca:
* Frequência respiratória:
* Temperatura:
* Peso:

**Mama:**
* Inspeção estática e dinâmica: Presença de nódulos, abaulamentos, retrações, alterações no mamilo.
* Palpação: Nódulos, sensibilidade, descarga papilar.

**Abdome:**
* Inspeção estática e dinâmica: Cicatrizes, abaulamentos, circulação colateral.
* Palpação: Dor, massas, visceromegalias.
* Ausculta: Presença de ruídos hidroaéreos.

**Ginecológico:**
* Inspeção estática e dinâmica: Aspecto da mucosa, presença de lesões, cistocele, retocele, perda urinária com Valsalva.
* Toque vaginal: Sensibilidade, tamanho e posição do útero, palpação de anexos.

## 9. Exames Complementares (Se Trazidos)
* Exames laboratoriais (hemograma, glicemia, função hepática).
* Ultrassonografia transvaginal ou abdominal.
* Mamografia.
* Citologia oncótica (Papanicolau).

## 10. Hipótese Diagnóstica
* Diagnósticos baseados na anamnese, exame físico e exames complementares.

## 11. Conduta
* Medicações: Nome, dose, frequência.
* Orientações: Alimentação, atividade física, higiene íntima, etc.
* Solicitação de exames adicionais: Se necessário, detalhar.
* Acompanhamento: Data de retorno e sinais de alerta.
  `,
  return: `Consulta de Retorno - Ginecologia e Obstetrícia

## 1. Lista de Problemas e HDA Prévia
* Descreva aqui os problemas identificados em consultas anteriores e um breve resumo da evolução e conduta adotada.

## S - Subjetivo
**Motivo do retorno:**
* Retorno para acompanhamento de tratamento ou cirurgia.
* Apresentação de queixas novas ou persistentes.

**Avaliação da evolução:**
* Melhora ou piora dos sintomas anteriores:
* Perguntar sobre alterações na intensidade, frequência e duração.
* Persistência de sintomas:
* Exemplo: dor pélvica, corrimento vaginal, prurido, alterações menstruais, ou dispareunia.

**Adesão ao tratamento:**
* Medicações:
* Está seguindo a prescrição corretamente? Houve dificuldade no uso ou efeitos colaterais?
* Mudanças no estilo de vida:
* Melhorou a alimentação? Está realizando higiene íntima adequada? Incorporou exercícios físicos?

**Sintomas atuais:**
* Genitais: Persistência ou surgimento de corrimento, sangramento anormal, ou dor vaginal.
* Menstruais: Alterações no fluxo (aumento/diminuição), duração ou frequência.
* Sexuais: Alterações na libido, dor durante a relação sexual (dispareunia), ou dificuldade em atingir orgasmo.
* Urinários: Presença de dor ao urinar (disúria), urgência urinária ou incontinência.
* Gastrointestinais: Constipação, dor abdominal, ou alterações nas evacuações.

**Impacto funcional:**
* Como os sintomas estão afetando o cotidiano da paciente (trabalho, vida social, vida sexual, sono e qualidade de vida)?

## O - Objetivo
**Sinais Vitais:**
* Pressão arterial:
* Frequência cardíaca:
* Frequência respiratória:
* Temperatura:
* Peso: (comparar com consulta anterior).
* Altura:
* Índice de Massa Corporal (IMC):

**Exames solicitados (se aplicável):**
* Ultrassonografia:
* Alterações na anatomia uterina, presença de cistos ovarianos, miomas ou massas pélvicas.
* Citologia oncótica (Papanicolau):
* Resultado e descrição (ex.: inflamação, presença de infecção, alterações celulares).
* Mamografia:
* Classificação BIRADS e achados.
* Exames laboratoriais:
* Hemograma, perfil hormonal, glicemia, função hepática.

**Exame físico:**
* Inspeção geral:
* Palidez, icterícia, emagrecimento, hidratação, ou alterações na pele.
* Mama:
* Inspeção estática e dinâmica (abaulamentos, retrações, alterações nos mamilos).
* Palpação (presença de nódulos ou dor).
* Abdome:
* Inspeção (cicatrizes, abaulamentos, circulação colateral).
* Palpação (presença de dor, massas ou visceromegalias).
* Ausculta (ruídos hidroaéreos).
* Ginecológico:
* Inspeção estática: Aspecto da vulva, grandes e pequenos lábios, presença de lesões ou sinais de infecção.
* Inspeção dinâmica: Manobra de Valsalva para verificar cistocele, retocele, ou perda urinária.
* Toque vaginal: Presença de dor, mobilidade uterina, tamanho e consistência do útero, palpação de anexos.

## A - Avaliação
**Diagnóstico evolutivo ou definitivo:**
* Atualizar o diagnóstico baseado na evolução clínica e nos resultados de exames.
* Determinar se a condição está estável, em melhora, ou se houve piora.

**Resposta ao tratamento:**
* A paciente respondeu ao tratamento conforme esperado?
* Houve necessidade de ajustar ou suspender medicações?

**Diagnósticos diferenciais:**
* Considerar condições adicionais ou novas que possam justificar sintomas persistentes.

## P - Plano
**Condutas terapêuticas:**
* Medicações:
* Ajustar doses, prescrever novas medicações ou suspender tratamentos anteriores.
* Tratamento tópico ou local:
* Cremes, óvulos, ou terapias para condições específicas (ex.: candidíase, vaginose bacteriana).

**Solicitação de exames adicionais:**
* Caso necessário, solicitar exames para aprofundar a avaliação:
* Nova ultrassonografia transvaginal.
* Perfil hormonal (estradiol, progesterina, FSH, LH).
* Ressonância magnética pélvica (se indicado).
* Exames laboratoriais adicionais (ex.: função tireoidiana, vitamina D).

**Orientações:**
* Higiene íntima: Reforçar a importância de cuidados adequados para evitar infecções.
* Dieta: Orientar consumo de fibras e hidratação adequada para melhorar a constipação (se aplicável).
* Atividade física: Recomendação de exercícios moderados e regulares.
* Sinais de alerta: Informar quais sintomas exigem retorno imediato (ex.: febre alta, sangramento intenso, dor abdominal súbita).

**Planejamento preventivo:**
* Exames anuais: Agendar mamografia, ultrassonografia e citologia oncótica de rotina.
* Imunizações: Verificar e orientar sobre vacinas necessárias (ex.: HPV, hepatite B).

**Acompanhamento:**
* Retorno: Agendar nova consulta para revisão dos sintomas e resultados.
* Intervalo sugerido: 4 a 6 semanas (ou conforme a necessidade).
  `,
},

"Cardiologia": {
  first: `Anamnese para Primeira Consulta - Cardiologia

## 1. Identificação
* **Nome:**
* **Idade:**
* **Sexo:**
* **Estado Civil:**
* **Profissão:**
* **Endereço:**
* **Telefone:**
* **Data da Consulta:**

## 2. Queixa Principal (QP)
**Motivo principal da consulta:**
> (Descrever em uma frase curta utilizando as palavras do paciente, como "Dor no peito há 1 semana" ou "Palpitações ao esforço físico.")

## 3. História da Doença Atual (HDA)
**Início dos sintomas:**
* Quando começaram? (Data ou estimativa).
* Foi súbito ou gradual?
* Havia algum fator associado no início (ex.: esforço físico, estresse emocional, infecção)?

**Características dos sintomas:**
* **Dor no peito:** Localização, irradiação (braço esquerdo, mandíbula, costas), tipo (pontada, pressão, queimação), intensidade (escala de 0-10), duração (em segundos, minutos, horas).
* **Palpitações:** Frequência (diária, semanal), duração dos episódios, sensação de batimentos rápidos ou irregulares, relação com esforço ou repouso.
* **Falta de ar:** Piora ao esforço, ao deitar (ortopneia) ou durante a noite (dispneia paroxística noturna).
* **Edema:** Inchaço em pernas ou tornozelos, melhora com repouso ou elevação dos membros.
* **Outros sintomas associados:** Tonturas, síncopes, sudorese, náuseas, fadiga.

**Evolução dos sintomas:**
* Sintomas estão melhorando, piorando ou permanecem iguais?
* Ocorrem com maior frequência ou intensidade?
* Há períodos de remissão?

**Fatores desencadeantes/piora:**
* Esforço físico, estresse emocional, refeições, mudanças posturais (levantar, deitar).

**Fatores de alívio:**
* Repouso, medicação (especificar qual), compressa, elevação dos membros, etc.

**Sintomas associados gerais:**
* Febre, tosse, hemoptise, perda de peso inexplicável, desconforto abdominal, entre outros.

**Tratamentos prévios:**
* Medicamentos usados anteriormente (dose, frequência e resposta).
* Intervenções não farmacológicas (ex.: repouso, mudanças na dieta).

## 4. Antecedentes Pessoais
**Doenças prévias:**
* Hipertensão arterial, diabetes, dislipidemia, infarto, arritmias, insuficiência cardíaca, doenças reumatológicas, histórico de febre reumática.

**Histórico cirúrgico:**
* Cirurgias cardíacas (angioplastia, troca valvar, etc.) ou outras intervenções (cateterismo, implante de stent).

**Alergias:**
* Medicamentos, alimentos ou substâncias químicas.

**Uso de medicamentos:**
* Nome, dose, frequência e tempo de uso.

**Hábitos de vida:**
* **Tabagismo:** Fuma atualmente? Se sim, há quanto tempo e quantos cigarros por dia?
* **Etilismo:** Frequência e quantidade de álcool ingerido.
* **Atividade física:** Realiza exercícios regularmente? Tipo, intensidade e frequência.
* **Dieta habitual:** Consumo de sal, gorduras, açúcares e fibras.

## 5. Antecedentes Familiares
**Histórico de doenças cardíacas:**
* Infarto, hipertensão arterial, AVC, diabetes, arritmias, dislipidemia.
* Parentes afetados: Pai, mãe, irmãos, avós (incluindo idades de ocorrência).

## 6. Revisão de Sistemas (ISDA)
**Cardiovascular:**
* Dor no peito, palpitações, falta de ar, inchaço nas pernas, desmaios.
**Respiratório:**
* Tosse, falta de ar, chiado, dor torácica ao respirar profundamente.
**Neurológico:**
* Tonturas, desmaios, perda de força, alterações de memória.
**Gastrointestinal:**
* Náuseas, vômitos, desconforto abdominal.
**Outros:**
* Febre, sudorese noturna, perda de peso sem causa aparente.

## 7. Exame Físico
**Sinais Vitais:**
* Pressão arterial:
* Frequência cardíaca:
* Frequência respiratória:
* Temperatura:
* Peso:
* Altura:
* Índice de Massa Corporal (IMC):

**Inspeção Geral:**
* Palidez, cianose, edema, sudorese, emagrecimento.

**Exame cardiovascular:**
* **Ausculta cardíaca:** Bulhas normofonéticas, ritmo regular, ausência de sopros ou arritmias.
* **Palpação:** Ictus cordis normodinamogênico, localizado no 5º espaço intercostal esquerdo.
* **Pulsos:** Presentes e simétricos em todas as extremidades.

**Exame pulmonar:**
* Ausculta normal, sem ruídos adventícios.

**Exame abdominal:**
* Sem hepatomegalia, ascite ou dor à palpação.

## 8. Exames Complementares Trazidos pelo Paciente
* **Eletrocardiograma (ECG):**
* **Ecocardiograma:**
* **Exames laboratoriais:**

## 9. Hipótese Diagnóstica
* Diagnóstico provável ou diferencial baseado nas informações obtidas.
* Risco cardiovascular de acordo com EMAPO, LEE ou GOLDMAN?

## 10. Plano Terapêutico
**Conduta terapêutica inicial:**
* Medicamentos indicados (nome, dose, frequência).
* Reforçar mudanças no estilo de vida e dieta.

**Exames complementares adicionais:**
* MAPA, Holter, teste ergométrico, angiografia (se necessário).

**Educação:**
* Explicar ao paciente a importância do tratamento.

**Acompanhamento:**
* Retorno para revisão com novos exames em 4-6 semanas.
  `,
  return: `Consulta de Retorno - Cardiologia

## 1. Lista de Problemas e HDA Prévia
* Sintetizar as condições já diagnosticadas e a evolução desde a consulta anterior.

## S - Subjetivo
**Avaliação dos sintomas:**
* Melhoraram, pioraram ou persistem?
* Houve redução na frequência ou intensidade?

**Adesão ao tratamento:**
* Uso regular de medicações?
* Mudanças na dieta e estilo de vida?

**Sintomas atuais:**
* Persistência de dor torácica, palpitações, falta de ar, edemas, tonturas ou desmaios.
* Novos sintomas (ex.: fadiga, ganho de peso, tosse).

**Impacto funcional:**
* Alterações na qualidade de vida (trabalho, sono, atividades diárias).

## O - Objetivo
**Sinais Vitais:**
* Pressão arterial:
* Frequência cardíaca:
* Peso:

**Resultados de exames solicitados:**
* ECG, ecocardiograma, exames laboratoriais, etc.

**Exame físico:**
* Palpação e ausculta cardíaca.
* Inspeção de sinais periféricos (edema, cianose).

## A - Avaliação
**Diagnóstico atualizado:**
* Evolução da condição: melhora, piora ou estabilidade?

## P - Plano
**Conduta terapêutica:**
* Ajuste de medicações, inclusão de novos fármacos.

**Exames adicionais:**
* MAPA, Holter, novos exames laboratoriais.

**Educação:**
* Reforçar adesão ao tratamento e orientações.

**Acompanhamento:**
* Data de retorno e sinais de alerta para reavaliação precoce.
  `,
},

"Gastroenterologia": {
  first: `Anamnese para Primeira Consulta - Gastroenterologia

## 1. Identificação
**Nome:**
**Idade:**
**Sexo:**
**Estado Civil:**
**Profissão:**
**Endereço:**
**Telefone:**
**Data da Consulta:**

## 2. Queixa Principal (QP)
**Motivo principal da consulta:**
> (Descrever em uma frase objetiva, utilizando as palavras do paciente, por exemplo: "Dor abdominal há 3 meses.")

## 3. História da Doença Atual (HDA)
**Início dos sintomas:**
* Quando começaram? (Data exata ou estimada).
* Foi súbito ou gradual?
* Havia algum evento associado no momento (mudança alimentar, viagens, estresse, infecções, etc.)?

**Características dos sintomas:**
* **Localização:** Onde é a dor ou desconforto?
* **Irradiação:** A dor se espalha para algum outro local (ex.: costas, peito, braço)?
* **Intensidade:** Escala de 0 a 10.
* **Tipo de dor ou desconforto:** Queimação, pontada, peso, pressão, cólica.
* **Duração:** Quanto tempo dura o sintoma? É constante ou intermitente?
* **Horário:** Em qual período do dia os sintomas predominam (manhã, tarde, noite, após refeições)?

**Evolução dos sintomas:**
* Desde o início, os sintomas estão melhorando, piorando ou permanecem iguais?
* Algum episódio semelhante no passado?

**Fatores desencadeantes/piora:**
* Quais situações ou ações agravam o quadro?
  * Exemplo: Alimentação (gorduras, frituras, café, álcool, laticínios), estresse, esforço físico, posição (deitado, inclinado), horário do dia, jejum prolongado.

**Fatores de alívio:**
* Quais situações ou ações melhoram o quadro?
  * Exemplo: Medicamentos, jejum, repouso, postura (levantar, deitar, inclinar), atividade física leve, compressas, alimentação leve, etc.

**Sintomas associados:**
* Presença de sintomas como náuseas, vômitos, azia, eructações (arroto), flatulências, sensação de peso no estômago, diarreia, constipação, sangue nas fezes ou alterações na cor das fezes.
* Algum sintoma geral, como febre, cansaço, perda de peso ou fraqueza?

**Tratamentos prévios:**
* Medicamentos já utilizados: nome, dose, duração, e resposta.
* Tentou algum remédio caseiro ou intervenção não farmacológica?

## 4. Antecedentes Pessoais
**Doenças crônicas:**
* Diabetes, hipertensão, dislipidemia, doenças hepáticas (esteatose, hepatites), doenças autoimunes, doenças gastrointestinais prévias (gastrite, úlceras, esofagite, doença de Crohn, colite).

**Histórico cirúrgico:**
* Cirurgias abdominais (colecistectomia, hérnias, bariátrica, etc.) ou outras intervenções.
* Data e motivo da cirurgia.

**Alergias:**
* Medicamentos, alimentos, produtos tópicos ou substâncias inalatórias.

**Uso de medicamentos:**
* Nome, dose, frequência e tempo de uso (ex.: antiácidos, IBPs, anti-inflamatórios, laxantes).

**Hábitos de vida:**
* **Tabagismo:** Fuma atualmente? Há quanto tempo? Quantidade por dia. Se parou, há quanto tempo?
* **Etilismo:** Consome álcool? Tipo, frequência, quantidade.
* **Atividade física:** Pratica regularmente? Tipo, frequência e intensidade.
* **Dieta habitual:**
    * Frequência das refeições, consumo de alimentos ricos em gorduras, frituras, condimentos, cafeína.
    * Consumo de fibras e líquidos.
    * Horários das refeições (tarde da noite, intervalos longos, etc.).

## 5. Antecedentes Familiares
**Histórico de doenças familiares:**
* Câncer gástrico, câncer de cólon, diabetes, hipertensão, doenças gastrointestinais crônicas, dislipidemia.
* Parentes afetados: Pai, mãe, irmãos, avós.

## 6. Revisão de Sistemas (ISDA)
**Gastrointestinal:**
* Dor abdominal, azia, náuseas, vômitos, diarreia, constipação, sangramentos, alteração na cor das fezes.
**Cardiovascular:**
* Dor no peito, palpitações, cansaço aos esforços, inchaço nas pernas.
**Respiratório:**
* Tosse, falta de ar, chiado, dor torácica ao respirar profundamente.
**Neurológico:**
* Tonturas, cefaleia, desmaios, alterações sensoriais ou motoras.
**Endócrino:**
* Perda ou ganho de peso inexplicado, aumento de sede, fome ou frequência da urina.
**Geniturinário:**
* Alterações urinárias, dor ao urinar, urgência, sangue na urina.

## 7. Exame Físico
**Sinais Vitais:**
* Pressão arterial:
* Frequência cardíaca:
* Frequência respiratória:
* Temperatura:
* Peso:
* Altura:
* IMC:

**Inspeção Geral:**
* Presença de palidez, icterícia, emagrecimento, cianose.

**Abdômen:**
* **Inspeção:** Cicatrizes, abaulamentos, peristaltismo visível.
* **Palpação:** Dor (localizada ou difusa), massas, visceromegalias.
* **Percussão:** Presença de som timpânico ou macicez.
* **Ausculta:** Ruídos hidroaéreos (normais, ausentes ou aumentados).

## 8. Exames Complementares Trazidos pelo Paciente
* Exames laboratoriais (hemograma, glicemia, função hepática, etc.).
* Endoscopia digestiva alta: achados relevantes.
* Imagens (USG, tomografia): descrever alterações.

## 9. Hipótese Diagnóstica
* Suspeitas baseadas na história clínica, exame físico e exames trazidos.

## 10. Plano Terapêutico
**Exames Complementares:**
* Exames adicionais necessários.

**Condutas Imediatas:**
* Prescrição de medicamentos.
* Orientação dietética e comportamental (evitar alimentos desencadeantes, fracionar refeições, etc.).

**Educação do Paciente:**
* Explicação sobre a condição suspeita, importância do tratamento e possíveis complicações.

**Acompanhamento:**
* Data do retorno.
* Critérios de reavaliação em caso de piora dos sintomas.
  `,
  return: `Consulta de Retorno - Gastroenterologia

## 1. Lista de Problemas e HDA Prévia
* Apresentar os problemas identificados anteriormente e o resumo da evolução desde a última consulta.

## S - Subjetivo

### 1. Avaliação dos Sintomas
* Os sintomas iniciais melhoraram, pioraram ou persistem?
  * Se melhoraram: Em que grau? Total, parcial ou leve?
  * Se pioraram: Alguma relação com alimentação, medicações ou hábitos recentes?
* Os sintomas ocorrem com menor, mesma ou maior intensidade?
  * Escala de intensidade (0 a 10): Alterou-se desde a última consulta?
  * Houve mudanças na frequência ou duração dos sintomas?
  * Ocorrem mais vezes ao longo do dia? Persistem por mais ou menos tempo?

### 2. Adesão ao Tratamento
* **Orientações alimentares:**
  * Está seguindo a dieta recomendada? Quais alimentos foram evitados ou ainda causam desconforto?
* **Medicações prescritas:**
  * Está utilizando as medicações corretamente? Quais foram usadas e em que doses?
  * Houve esquecimento ou interrupção no uso das medicações?
* **Mudanças no estilo de vida:**
  * Reduziu ou cessou o consumo de álcool?
  * Está evitando o tabagismo?
  * Incorporou exercícios físicos regulares?

### 3. Sintomas Atuais
* **Persistência de sintomas iniciais:**
  * **Dor abdominal:** Localização, tipo (pontada, queimação), relação com refeições ou evacuações.
  * **Azia** (lembrar de usar "pirose" na descrição técnica, se o paciente mencionou "azia", transcrever entre aspas), **náuseas ou vômitos:** Frequência e intensidade.
  * **Alterações intestinais:** Diarreia ou constipação persistem? Houve mudança na frequência, consistência ou cor das fezes?
* **Surgimento de novos sintomas:**
  * Presença de febre, sangramentos gastrointestinais (vômito com sangue ou fezes escurecidas).
  * Perda de peso inexplicável ou cansaço extremo.

### 4. Impacto Funcional
* **Qualidade de vida:**
  * O paciente relata se sentir melhor, igual ou pior desde o início do tratamento?
* **Alterações no apetite:**
  * Está conseguindo se alimentar normalmente? Alguma aversão alimentar?
* **Energia e humor:**
  * Nota cansaço excessivo, irritabilidade ou falta de disposição?

## O - Objetivo

### 1. Sinais Vitais
* **Pressão arterial:**
* **Frequência cardíaca:**
* **Frequência respiratória:**
* **Temperatura:**
* **Peso atual:** (Comparar com a consulta anterior e observar variações significativas).

### 2. Resultados de Exames Solicitados
* **Laboratoriais:**
  * Hemograma completo, função hepática, perfil lipídico, glicemia.
  * Achados relevantes, como anemia ou elevações enzimáticas.
* **Endoscopia Digestiva Alta:**
  * Diagnósticos como gastrite, esofagite, presença de úlceras ou infecção por H. pylori.
* **Ultrassonografia/Imagens:**
  * Alterações anatômicas, como fígado esteatótico ou massas abdominais.
* **Teste de H. pylori:**
  * Resultado positivo/negativo e implicações no plano terapêutico.

### 3. Exame Físico
* **Inspeção Geral:**
  * Palidez, icterícia, emagrecimento, sinais de desidratação.
* **Palpação Abdominal:**
  * **Presença de dor:** Localização e intensidade.
  * **Massas ou visceromegalias:** Palpáveis ou ausentes.
* **Percussão:**
  * Presença de macicez ou som timpânico.
* **Ausculta:**
  * Ruídos hidroaéreos normais, aumentados ou ausentes.

## A - Avaliação
* **Diagnóstico Definitivo ou Evolutivo:**
  * Atualizar o diagnóstico com base na evolução clínica e resultados dos exames.
  * Determinar se o quadro está: dentro do esperado, estacionado ou agravado.
* **Resposta ao Tratamento:**
  * Houve melhora, estabilidade ou piora com a terapia inicial?
  * Identificar possíveis barreiras para o sucesso terapêutico (adesão, efeitos adversos).

## P - Plano

### 1. Conduta Terapêutica
* **Medicações:**
  * Ajustar doses, incluir ou retirar medicações conforme a evolução.
  * (Ex.: Omeprazol, antibióticos para erradicação de H. pylori, probióticos, laxantes).
* **Dieta:**
  * Reforçar orientações alimentares: evitar alimentos desencadeantes (gorduras, cafeína, condimentos).
  * Incentivar maior consumo de fibras, líquidos e refeições fracionadas.
* **Estilo de Vida:**
  * Continuar evitando tabagismo e consumo de álcool.
  * Incentivar atividade física leve e regular.

### 2. Solicitação de Exames Adicionais
* **Exames laboratoriais:** Controle de anemia, função hepática, ou glicemia (conforme necessidade).
* **Endoscopia de controle:** Se sintomas persistirem após tratamento de H. pylori ou gastrite.
* **Imagem adicional:** Ultrassom abdominal, tomografia ou ressonância magnética para esclarecimento de achados.

### 3. Educação e Orientações
* **Resultados dos exames:** Explicar o que os exames indicam e como isso afeta o tratamento.
* **Sinais de alerta:** Reforçar os sintomas que exigem reavaliação imediata:
  * Dor abdominal intensa, febre alta, vômito com sangue, ou emagrecimento rápido.
* **Adesão ao tratamento:** Reforçar a importância de seguir o plano terapêutico para evitar complicações futuras.

### 4. Acompanhamento
* **Nova data de retorno:** Agendar em 4-6 semanas ou conforme evolução clínica.
* **Critérios para reavaliação precoce:** Paciente deve retornar se houver piora significativa antes do próximo retorno agendado.
  `,
},

"Dermatologia": {
  first: `Anamnese para Primeira Consulta - Dermatologia

## 1. Identificação
* **Nome completo:**
* **Data de Nascimento:**
* **Idade:**
* **Sexo/Gênero:**
* **Estado Civil:**
* **Profissão:**
* **Naturalidade:**
* **Procedência:**
* **Endereço completo:**
* **Telefone(s) de contato:**
* **E-mail:**
* **Convênio/SUS:**
* **Quem indicou/Como chegou ao serviço:**
* **Data da Consulta:**

## 2. Queixa Principal (QP)
* **Motivo principal da consulta (registrar com as palavras do paciente e duração):**
  * Ex: "Manchas vermelhas que coçam nos braços há 1 mês."

## 3. História da Doença Atual (HDA)
* **Início da Lesão/Sintoma:**
  * Quando e como começou? (Data aproximada, início súbito ou gradual)
  * Fator desencadeante percebido (ex: novo medicamento, produto, alimento, exposição solar, estresse, picada de inseto, trauma local).
* **Características da Lesão/Sintoma:**
  * Localização inicial e atual (descrever todas as áreas afetadas).
  * Tipo de lesão percebida pelo paciente (mancha, caroço, bolha, ferida, etc.).
  * Aparência: cor, forma, tamanho (houve mudança?).
  * Sintomas associados à lesão:
    * Prurido (coceira): intensidade (0-10), período do dia em que é pior, fatores de melhora/piora.
    * Dor: tipo (queimação, pontada, etc.), intensidade (0-10).
    * Ardor, formigamento, sensação de calor, repuxamento.
    * Presença de secreção (cor, odor, quantidade).
    * Sangramento (espontâneo ou ao toque/trauma mínimo).
* **Evolução:**
  * As lesões aumentaram em número, tamanho ou mudaram de aspecto?
  * Espalhou-se para outras áreas? Qual a progressão?
  * Houve períodos de melhora ou piora espontânea?
* **Fatores de Melhora/Piora:**
  * O que melhora os sintomas/lesões (ex: frio, calor, repouso, alguma medicação)?
  * O que piora os sintomas/lesões (ex: calor, suor, sol, certos tecidos, alimentos, estresse)?
* **Tratamentos Anteriores (para esta queixa):**
  * Quais medicamentos (tópicos ou orais) já utilizou? (Nome, dose, por quanto tempo, quem prescreveu).
  * Resultados obtidos com cada tratamento.
  * Uso de remédios caseiros ou produtos não prescritos.
* **Sintomas Sistêmicos Associados:**
  * Febre, calafrios, perda de peso inexplicada, fadiga, mal-estar geral.
  * Dor articular, muscular.
  * Outros sintomas relevantes.

## 4. Antecedentes Pessoais Patológicos (APP)
* **Doenças Cutâneas Prévias:**
  * Diagnósticos dermatológicos anteriores (acne, dermatite atópica, psoríase, vitiligo, micoses recorrentes, herpes, etc.).
  * Tratamentos realizados e resultados.
* **Alergias:**
  * Medicamentos (especificar qual e tipo de reação).
  * Alimentos.
  * Cosméticos, produtos de higiene, perfumes.
  * Metais (níquel, etc.), tecidos, látex.
  * Picadas de inseto.
  * Alergias ambientais (poeira, pólen, ácaros, pelo de animais).
* **Outras Doenças Crônicas e Condições:**
  * Diabetes Mellitus, Hipertensão Arterial Sistêmica.
  * Doenças da tireoide.
  * Doenças autoimunes (lúpus, artrite reumatoide, esclerodermia, etc.).
  * Doenças gastrointestinais (doença celíaca, DII).
  * Asma, rinite alérgica.
  * Condições psiquiátricas (ansiedade, depressão - podem influenciar ou ser agravadas por dermatoses).
  * Infecções crônicas (HIV, Hepatites B/C).
  * Histórico de câncer (qualquer tipo).
* **Medicações em Uso Contínuo (para quaisquer condições):**
  * Nome, dose, frequência, motivo do uso. (Incluir vitaminas, suplementos, fitoterápicos, anticoncepcionais).
* **Cirurgias Prévias:**
  * Tipo de cirurgia, data, motivo. (Especialmente cirurgias dermatológicas, remoção de nevos, câncer de pele).
  * Problemas de cicatrização (queloides, cicatrizes hipertróficas).
* **Imunizações:**
  * Vacinação para HPV, Herpes Zoster, Varicela, Gripe, Pneumocócica (conforme idade e risco).
* **Transfusões sanguíneas:** Sim/Não. Quando? Reações?
* **Internações hospitalares prévias:** Motivo, data.

## 5. Hábitos de Vida
* **Exposição Solar:**
  * Frequência e duração da exposição solar (diária, lazer, trabalho).
  * Horários de maior exposição.
  * Uso de protetor solar (FPS, frequência de aplicação, áreas de aplicação).
  * Uso de outras medidas de fotoproteção (chapéu, óculos, roupas).
  * Histórico de queimaduras solares (especialmente com bolhas, na infância/adolescência).
  * Bronzeamento artificial (já fez?).
* **Rotina de Cuidados com a Pele (Skincare):**
  * Produtos utilizados no rosto e corpo (sabonetes, hidratantes, ácidos, antioxidantes, maquiagem).
  * Frequência de uso.
* **Tabagismo:**
  * Sim/Não. Se sim, quantidade (cigarros/dia) e tempo de tabagismo. Se ex-tabagista, há quanto tempo parou.
* **Etilismo:**
  * Sim/Não. Se sim, tipo de bebida, frequência e quantidade.
* **Uso de Drogas Ilícitas:** Sim/Não. Quais?
* **Atividade Física:** Tipo, frequência, intensidade. (Transpiração excessiva? Dermatoses relacionadas ao esporte?)
* **Dieta:**
  * Avaliação geral da qualidade da dieta.
  * Percepção de alimentos que pioram/melhoram a pele.
  * Restrições alimentares.
* **Sono:** Qualidade e quantidade de horas de sono.
* **Nível de Estresse:** Percepção de estresse e como lida com ele.

## 6. Antecedentes Familiares
* **Doenças Cutâneas na Família:**
  * Psoríase, dermatite atópica, vitiligo, acne grave, rosácea.
  * Câncer de pele (melanoma, carcinoma basocelular, espinocelular) - quem na família e idade do diagnóstico.
  * Alopecias (calvície de padrão familiar, alopecia areata).
  * Doenças genéticas com manifestações cutâneas.
* **Histórico de Alergias ou Atopia na Família:** (Asma, rinite, dermatite).
* **Outras doenças relevantes (autoimunes, diabetes, etc.).**

## 7. História Ocupacional e Ambiental
* **Profissão Atual e Anteriores:**
  * Exposição a produtos químicos, irritantes, alérgenos, poeira, umidade, calor/frio extremo no trabalho.
* **Ambiente Doméstico:**
  * Contato com animais de estimação (tipo).
  * Presença de mofo, umidade em casa.
  * Hobbies que envolvam contato com substâncias específicas (jardinagem, pintura, etc.).
* **Viagens Recentes:** Para locais com dermatoses endêmicas ou diferentes condições climáticas.

## 8. Impacto Psicossocial da Doença
* **Como a condição de pele afeta:**
  * Qualidade de vida (escala de 0-10 ou percepção).
  * Autoestima, imagem corporal.
  * Humor (ansiedade, tristeza, isolamento).
  * Relações sociais, íntimas.
  * Atividades diárias, trabalho, lazer.
  * Sono (devido a prurido, dor).

## 9. Revisão de Sistemas (foco em manifestações sistêmicas associadas a dermatoses)
* **Geral:** Febre, calafrios, sudorese noturna, perda ou ganho de peso inexplicado, fadiga, astenia.
* **Olhos:** Olhos secos, vermelhidão, fotofobia, alterações visuais.
* **Nariz/Garganta:** Boca seca, úlceras orais/nasais, corrimento nasal, dor de garganta.
* **Cardiovascular:** Palpitações, dor torácica, edema de membros inferiores.
* **Respiratório:** Tosse, dispneia, chiado no peito.
* **Gastrointestinal:** Dor abdominal, diarreia, constipação, sangue nas fezes.
* **Músculo-esquelético:** Dores articulares, inchaço articular, rigidez matinal, fraqueza muscular.
* **Neurológico:** Cefaleia, tonturas, parestesias.
* **Geniturinário:** Úlceras genitais, corrimento.

## 10. Exame Dermatológico Detalhado
* **Inspeção Geral:** Fototipo de Fitzpatrick (I a VI). Estado de hidratação da pele.
* **Lesões Elementares:**
  * **Planas:** Mácula (alteração de cor), Mancha (mácula >1cm).
  * **Sólidas:** Pápula (elevada <1cm), Placa (elevada >1cm, confluência de pápulas), Nódulo (elevado >1cm, mais profundo), Tumor (nódulo >3cm), Goma (nódulo que amolece e ulcera), Vegetação (pápula pediculada, filiforme ou em couve-flor), Verrucosidade (pápula de superfície áspera).
  * **Conteúdo Líquido:** Vesícula (<1cm, líquido claro), Bolha (>1cm, líquido claro), Pústula (conteúdo purulento), Abscesso (coleção purulenta na derme/hipoderme).
  * **Perdas Teciduais:** Escama (laminada, furfurácea), Erosão (perda superficial), Ulceração (perda profunda), Fissura (linear).
  * **Outras:** Crosta, Escara, Atrofia, Cicatriz (normotrófica, hipertrófica, queloideana), Esclerose, Liquenificação, Telangiectasia, Petéquia, Equimose, Víbice.
* **Morfologia das Lesões:**
  * Forma (arredondada, ovalada, anular, arciforme, linear, irregular, umbilicada, herpetiforme).
  * Tamanho (medir em cm/mm).
  * Cor (eritematosa, violácea, acastanhada, hipocrômica, acrômica, amarelada, etc.).
  * Superfície (lisa, áspera, rugosa, brilhante, descamativa, queratósica, úmida, necrótica).
  * Bordas (nítidas, difusas, elevadas, infiltradas, regulares, irregulares).
  * Consistência à palpação (macia, firme, elástica, endurecida, flutuante).
* **Arranjo das Lesões:** Isoladas, agrupadas (cacho), lineares (fenômeno de Koebner?), anulares, serpiginosas, reticulares.
* **Distribuição das Lesões:**
  * Localizada (especificar região anatômica precisa).
  * Generalizada/Disseminada.
  * Simétrica/Assimétrica.
  * Regional (cabeça, tronco, membros).
  * Áreas fotoexpostas, áreas cobertas.
  * Áreas flexoras, extensoras.
  * Intertriginosas (axilas, virilhas, inframamária).
  * Seborreicas (face, couro cabeludo, tórax anterior).
* **Exame dos Anexos Cutâneos:**
  * **Cabelos e Couro Cabeludo:**
    * Densidade capilar (rarefeito, normal).
    * Distribuição (padrão de alopecia, se houver).
    * Brilho, espessura, quebra dos fios.
    * Presença de descamação, eritema, pústulas, cicatrizes no couro cabeludo.
    * Teste de tração (pull test), se indicado.
  * **Unhas (mãos e pés):**
    * Formato, espessura, superfície (lisa, rugosa, estriações, depressões puntiformes - pitting).
    * Cor da lâmina ungueal, leito ungueal.
    * Presença de onicólise, leuconíquia, melanoníquia, paroníquia, deformidades.
* **Exame das Mucosas:** Oral, genital, ocular (se queixa específica ou suspeita).
* **Exames Complementares Realizados no Consultório:**
  * **Dermatoscopia:** Descrição dos achados dermatoscópicos das lesões pigmentadas ou outras (padrões, cores, estruturas).
  * **Luz de Wood:** Áreas de fluorescência (útil em micoses, distúrbios de pigmentação, eritrasma).
  * **Citodiagnóstico de Tzanck:** (Suspeita de herpes, pênfigo).
  * **Micológico Direto (KOH):** (Suspeita de micoses superficiais).
  * **Estesiometria:** (Suspeita de hanseníase).

## 11. Exames Complementares Trazidos pelo Paciente
* Resultados de biópsias de pele anteriores (laudos histopatológicos).
* Exames laboratoriais (hemograma, bioquímica, sorologias, FAN, VHS, PCR, painel alérgico, dosagens hormonais, etc.).
* Culturas (micológico, bacteriológico) e antibiograma.
* Testes de contato (Patch tests).
* Outros exames de imagem relevantes.

## 12. Hipótese Diagnóstica (HD)
* Diagnóstico(s) principal(is) (justificar com base na anamnese e exame físico).
* Diagnósticos diferenciais (listar outras possibilidades).

## 13. Plano Terapêutico e Conduta
* **Tratamento Tópico:**
  * Medicamento(s): Princípio ativo, concentração, veículo (creme, pomada, loção, gel, solução).
  * Posologia: Como aplicar (quantidade, fina camada), quantas vezes ao dia, por quanto tempo.
  * Orientações específicas (aplicar após o banho, evitar áreas de dobra, etc.).
* **Tratamento Sistêmico:**
  * Medicamento(s): Nome, dose (mg, UI), via de administração.
  * Posologia: Quantas vezes ao dia, com ou sem alimentos, por quanto tempo.
  * Monitoramento necessário (exames laboratoriais antes/durante o uso).
  * Possíveis efeitos colaterais a observar.
* **Procedimentos Propostos (se indicado):**
  * Biópsia de pele (punch, shave, excisional - explicar necessidade e como é feito).
  * Crioterapia, eletrocauterização, curetagem, infiltração intralesional (explicar).
  * Outros (laser, peeling, etc.).
* **Orientações Gerais e Educação do Paciente:**
  * Cuidados gerais com a pele (higiene, hidratação).
  * Fotoproteção rigorosa (tipo de protetor, como usar).
  * Dieta (se houver relação com a dermatose).
  * Afastamento de fatores desencadeantes/agravantes identificados.
  * Informações sobre a doença (causa, curso natural, prognóstico, não contagiosidade - se for o caso).
  * Importância da adesão ao tratamento.
  * Sinais de alerta para procurar atendimento antes do retorno.
* **Solicitação de Exames Complementares Adicionais:**
  * Laboratoriais, biópsia, micológico, culturas, testes alérgicos, etc. (especificar e justificar).
* **Encaminhamentos:** Para outros especialistas, se necessário (reumatologista, alergologista, endocrinologista, cirurgião plástico, psicólogo).
* **Agendamento de Retorno:** Data/prazo para reavaliação.
`,
  return: `Consulta de Retorno - Dermatologia

## 1. Lista de Problemas e HDA Prévia
* Resumo dos diagnósticos e problemas ativos da consulta anterior.
* Plano terapêutico instituído na última consulta.

## S - Subjetivo
* **Motivo do Retorno:** (Agendado, reavaliação de tratamento, resultado de exames, piora do quadro, etc.).
* **Evolução da Queixa Principal desde a Última Consulta:**
  * Como o paciente se sentiu em relação à(s) lesão(ões) e sintomas?
  * **Melhora:**
    * Parcial ou total?
    * Em que aspecto melhorou (coceira, tamanho da lesão, vermelhidão, etc.)?
    * Percentual de melhora estimado pelo paciente.
  * **Piora:** Em que aspecto piorou? Novos sintomas? Novas lesões?
  * **Manutenção do quadro:** Sintomas e lesões inalterados.
* **Adesão ao Tratamento Prescrito:**
  * **Tratamento Tópico:**
    * Usou conforme orientado? (Produto, frequência, quantidade).
    * Esqueceu alguma aplicação? Teve dificuldades para aplicar?
  * **Tratamento Sistêmico:**
    * Tomou a medicação nos horários corretos? Esqueceu doses?
    * Interrompeu o uso por algum motivo?
  * **Orientações Gerais:**
    * Seguiu as recomendações de fotoproteção, dieta, cuidados com a pele?
* **Surgimento de Novas Lesões ou Sintomas:** Descrever localização, características.
* **Efeitos Colaterais das Medicações (se houver):**
  * Quais? Intensidade? O que fez a respeito?
* **Impacto na Qualidade de Vida:**
  * Houve melhora ou piora na qualidade de vida, autoestima, atividades diárias desde a última consulta?
* **Dúvidas do Paciente:** Anotar novas dúvidas ou preocupações.
* **Intervalo desde a última consulta:** (O que aconteceu nesse período? Alguma intercorrência não relacionada diretamente à queixa principal, mas que possa ser relevante?)

## O - Objetivo
* **Sinais Vitais:** (Aferir se relevante, especialmente se em uso de medicações sistêmicas que exijam monitoramento ou se houver queixa de febre, mal-estar).
* **Exame Dermatológico Comparativo:**
  * Reavaliar todas as áreas afetadas e descritas na consulta anterior.
  * Comparar o aspecto atual das lesões com a descrição ou fotos anteriores (se disponíveis).
    * Mudanças no tipo, número, tamanho, cor, forma, superfície, bordas das lesões.
  * Presença ou ausência de sinais de atividade da doença (eritema, descamação, novas pápulas, vesículas, etc.).
  * Sinais de melhora (clareamento, aplainamento, redução do eritema/descamação).
  * Sinais de piora (aumento, novas lesões, exsudação, infecção secundária).
  * Procurar por novas lesões em outras áreas.
  * Reavaliação de cabelos, unhas e mucosas, se pertinente ao caso.
  * **Dermatoscopia de Controle:** (Se realizada na consulta anterior para acompanhamento de lesões pigmentadas ou outras). Comparar achados.
* **Resultados de Exames Complementares Solicitados:**
  * Analisar laudos de biópsias, exames laboratoriais, culturas, testes alérgicos, etc.
  * Correlacionar os resultados com o quadro clínico.

## A - Avaliação
* **Diagnóstico Evolutivo:**
  * Confirmação ou modificação do diagnóstico inicial com base na evolução e/ou resultados de exames.
  * Doença em remissão, melhora significativa, melhora parcial, estável, em piora/agravamento, recidiva.
* **Resposta ao Tratamento:**
  * Satisfatória, parcial, insatisfatória.
  * Identificar possíveis causas para resposta inadequada (falha de adesão, diagnóstico incorreto, resistência ao tratamento, dose inadequada, interação medicamentosa, fator agravante não controlado).
* **Tolerância à Medicação:**
  * Bem tolerada, presença de efeitos colaterais (grau, manejo).
* **Avaliação de Possíveis Complicações:** (Infecção secundária, farmacodermia, etc.).
* **Necessidade de Ajuste no Plano Terapêutico:**

## P - Plano
* **Tratamento Tópico e/ou Sistêmico:**
  * **Manter:** Se resposta satisfatória e tratamento ainda necessário.
  * **Ajustar:**
    * Dose (aumentar, diminuir).
    * Frequência de uso.
    * Trocar princípio ativo ou veículo.
    * Adicionar nova medicação.
  * **Suspender:** Se remissão completa ou tratamento concluído. Considerar terapia de manutenção, se aplicável.
  * **Introduzir Nova Terapia:** Se resposta insatisfatória ou necessidade de outra abordagem.
* **Manejo de Efeitos Colaterais:** Orientar sobre como lidar ou prescrever tratamento para os efeitos.
* **Orientações e Reforço Educativo:**
  * Reafirmar a importância da adesão.
  * Esclarecer dúvidas.
  * Ajustar orientações de fotoproteção, cuidados com a pele, conforme a evolução.
  * Informações sobre a doença (causa, curso natural, prognóstico, não contagiosidade - se for o caso).
  * Importância da adesão ao tratamento.
  * Sinais de alerta para procurar atendimento antes do retorno.
* **Solicitação de Novos Exames:** (Se necessário para monitoramento, reavaliação diagnóstica ou para investigar comorbidades/efeitos colaterais).
* **Agendamento de Procedimentos:** (Se indicados e não realizados anteriormente, ou se necessitam de nova sessão).
* **Acompanhamento:**
  * Definir intervalo para o próximo retorno.
  * Critérios para reavaliação precoce (sinais de piora, efeitos colaterais graves).
* **Encaminhamentos:** Se surgirem novas necessidades.
`,
},

"Endocrinologia e Metabologia": {
  first: `Anamnese para Primeira Consulta - Endocrinologia e Metabologia

## 1. Identificação
* **Nome completo:**
* **Data de Nascimento:**
* **Idade:**
* **Sexo/Gênero:**
* **Estado Civil:**
* **Profissão:**
* **Naturalidade:**
* **Procedência:**
* **Endereço completo:**
* **Telefone(s) de contato:**
* **E-mail:**
* **Convênio/SUS:**
* **Quem indicou/Como chegou ao serviço:**
* **Data da Consulta:**

## 2. Queixa Principal (QP)
* **Motivo principal da consulta (registrar com as palavras do paciente e duração):**
  * Ex: "Açúcar alto no sangue há 6 meses", "Dificuldade para emagrecer há anos", "Nódulo no pescoço encontrado em exame de rotina."

## 3. História da Doença Atual (HDA)
* **Detalhar a(s) queixa(s) principal(is):**
  * **Início:** Quando e como os sintomas/alterações começaram? Fator desencadeante?
  * **Características dos Sintomas:**
    * **Relacionados ao Metabolismo de Carboidratos:** Sede excessiva (polidipsia), aumento do volume urinário (poliúria), aumento da fome (polifagia), visão turva, formigamentos (parestesias), infecções frequentes, dificuldade de cicatrização, hálito cetônico.
    * **Relacionados ao Peso Corporal:** Ganho ou perda de peso (quantos kg em quanto tempo? intencional ou não?), mudanças no apetite, distribuição da gordura corporal (central, periférica).
    * **Relacionados à Tireoide:**
      * **Hipotireoidismo (sugestivo):** Fadiga, sonolência, pele seca, queda de cabelo, unhas fracas, intolerância ao frio, constipação intestinal, ganho de peso, edema, bradicardia, alterações de humor (depressão), dificuldade de concentração.
      * **Hipertireoidismo (sugestivo):** Agitação, insônia, pele quente e úmida, queda de cabelo, intolerância ao calor, diarreia, perda de peso (com apetite aumentado ou normal), taquicardia, palpitações, tremores, bócio, exoftalmia.
      * Presença de nódulo no pescoço (dor, crescimento, rouquidão, dificuldade para engolir/respirar).
    * **Relacionados às Adrenais/Cortisol:** Fadiga, fraqueza muscular, alterações de peso (ganho centrípeto), estrias violáceas, equimoses fáceis, hipertensão, irregularidade menstrual, hirsutismo, acne, alterações de humor, hiperpigmentação cutânea (doença de Addison).
    * **Relacionados ao Metabolismo Ósseo:** Dores ósseas, fraturas por baixo impacto, perda de altura.
    * **Relacionados ao Eixo Gonadal:**
      * **Mulheres:** Irregularidade menstrual (amenorreia, oligomenorreia), infertilidade, sintomas de hiperandrogenismo (hirsutismo, acne, alopecia androgenética), galactorreia, fogachos, secura vaginal.
      * **Homens:** Diminuição da libido, disfunção erétil, infertilidade, ginecomastia, diminuição de pelos corporais/barba, perda de massa muscular.
    * **Relacionados ao Crescimento e Desenvolvimento (Crianças/Adolescentes):** Alterações na velocidade de crescimento (baixa estatura, alta estatura), puberdade precoce ou atrasada.
    * **Outros Sintomas:** Sudorese excessiva, alterações de humor (ansiedade, depressão, irritabilidade), alterações do sono, câimbras.
  * **Evolução dos Sintomas:** Progressivos, intermitentes, estáveis?
  * **Fatores de Melhora/Piora:** O que alivia ou agrava os sintomas?
  * **Tratamentos Anteriores (para esta queixa):** Medicações, dietas, cirurgias. Resultados?
  * **Investigações Prévias:** Exames laboratoriais, de imagem. Trazer resultados.
  * **Impacto nas Atividades Diárias e Qualidade de Vida.**

## 4. Antecedentes Pessoais Patológicos (APP)
* **Doenças Endocrinológicas Previamente Diagnosticadas:**
  * Diabetes Mellitus (Tipo 1, Tipo 2, Gestacional, LADA, MODY). Data do diagnóstico. Complicações (retinopatia, nefropatia, neuropatia, doença cardiovascular). Monitoramento glicêmico (frequência, valores). Episódios de hipo/hiperglicemia severa.
  * Doenças da Tireoide (hipotireoidismo, hipertireoidismo, Doença de Graves, Tireoidite de Hashimoto, nódulos tireoidianos, câncer de tireoide). Data do diagnóstico. Tratamentos (medicamentos, iodo radioativo, cirurgia).
  * Doenças das Paratireoides (hiperparatireoidismo, hipoparatireoidismo).
  * Doenças das Adrenais (Insuficiência adrenal, Síndrome de Cushing, feocromocitoma, hiperaldosteronismo, tumores).
  * Doenças da Hipófise (adenomas, hipopituitarismo, diabetes insipidus, acromegalia, Doença de Cushing).
  * Osteoporose/Osteopenia. Densitometrias ósseas prévias.
  * Dislipidemia (colesterol alto, triglicerídeos altos).
  * Obesidade. Tratamentos prévios.
  * Outras síndromes ou doenças metabólicas.
* **Outras Doenças Crônicas:** Hipertensão arterial, cardiopatias, doenças renais, hepáticas, autoimunes, reumatológicas, psiquiátricas.
* **Cirurgias Prévias:** Especialmente tireoidectomia, paratireoidectomia, cirurgia bariátrica, adrenalectomia, hipofisectomia, ooforectomia, orquiectomia. Data e motivo.
* **Internações Hospitalares Prévias:** Motivo, data.
* **Alergias:** Medicamentos, alimentos, contraste.
* **Medicações em Uso Contínuo:** Nome, dose, posologia, motivo. (Incluir hormônios, hipoglicemiantes, anti-hipertensivos, estatinas, vitaminas, suplementos, fitoterápicos, corticoides).
* **Imunizações:** (Importante em diabéticos – gripe, pneumonia).

## 5. História Alimentar e de Atividade Física
* **Hábitos Alimentares:**
  * Recordatório alimentar das últimas 24h ou diário alimentar típico.
  * Número de refeições/dia, horários. Omitir refeições?
  * Consumo de frutas, verduras, legumes, fibras, laticínios, carnes, gorduras, açúcares, alimentos processados/ultraprocessados, bebidas açucaradas, adoçantes artificiais.
  * Hidratação (consumo de água).
  * Restrições alimentares (médicas, por opção). Intolerâncias.
  * Quem prepara as refeições? Comer fora de casa com que frequência?
* **Histórico de Peso:** Peso máximo e mínimo na vida adulta. Flutuações de peso. Tentativas prévias de emagrecimento (dietas, medicamentos – quais?). Resultados.
* **Atividade Física:**
  * Pratica? Qual tipo? Frequência (vezes/semana)? Duração (minutos/sessão)? Intensidade (leve, moderada, vigorosa)?
  * Barreiras para atividade física. Sedentarismo.

## 6. Antecedentes Ginecológicos e Obstétricos (para mulheres)
* **Menarca:** Idade da primeira menstruação.
* **Ciclos Menstruais:** Duração, intervalo (regularidade – ex: 28/28 dias, irregular), fluxo (pequeno, normal, aumentado), cólicas. Data da Última Menstruação (DUM).
* **Sintomas de Hiperandrogenismo:** Hirsutismo (aumento de pelos em face, tórax, abdômen – usar escala de Ferriman-Gallwey se familiarizado), acne (tipo, localização, tratamento prévio), alopecia androgenética (perda de cabelo padrão masculino).
* **Métodos Contraceptivos:** Atuais e prévios.
* **Vida Sexual:** Ativa? Dispareunia? Libido.
* **Gestação(ões):** Número (G), Partos (P), Abortos (A). Idade na primeira gestação.
  * Intercorrências nas gestações: Diabetes gestacional, hipertensão gestacional (pré-eclâmpsia), distúrbios tireoidianos. Peso dos recém-nascidos.
* **Lactação:** Amamentou? Por quanto tempo?
* **Menopausa:** Idade de início (se aplicável). Sintomas (fogachos, secura vaginal, alterações de humor, insônia). Uso de Terapia Hormonal (TH) – qual, dose, duração, motivo para interrupção.

## 7. Antecedentes Andrológicos (para homens)
* **Puberdade:** Idade de início dos caracteres sexuais secundários.
* **Libido:** Normal, diminuída, ausente.
* **Função Erétil:** Ereções matinais/noturnas? Dificuldade para obter/manter ereção?
* **Fertilidade:** Filhos? Tentativas de engravidar a parceira?
* **Sintomas de Hipogonadismo:** Diminuição de pelos corporais/faciais, perda de massa muscular, fadiga, ginecomastia, alterações de humor.
* **Doenças testiculares/prostáticas.**

## 8. Antecedentes Familiares
* **Pais, irmãos, filhos (idade, estado de saúde, causa de óbito se falecidos):**
* **Histórico de Doenças Endócrinas e Metabólicas na Família:**
  * Diabetes Mellitus (Tipo 1, Tipo 2, Gestacional) – quem?
  * Doenças da Tireoide (hipo/hipertireoidismo, bócio, nódulos, câncer) – quem?
  * Obesidade – quem?
  * Dislipidemia – quem?
  * Osteoporose/Fraturas por fragilidade – quem?
  * Baixa estatura/Alta estatura familiar. Puberdade precoce/atrasada.
  * Doenças da hipófise, adrenais (raro, mas perguntar se houver suspeita de síndromes genéticas como MEN).
  * Infertilidade. SOP.
* **Doenças Cardiovasculares Precoces:** (IAM, AVC antes dos 55 anos em homens ou 65 anos em mulheres).
* **Outras doenças relevantes:** Câncer, doenças autoimunes.
* **Consanguinidade entre os pais.**

## 9. História Psicossocial e Hábitos
* **Tabagismo:** Sim/Não. Carga tabágica (anos-maço). Ex-tabagista (quando parou).
* **Etilismo:** Sim/Não. Tipo, frequência, quantidade.
* **Uso de Drogas Ilícitas:** Sim/Não. Quais?
* **Sono:** Horas por noite. Qualidade (reparador?). Roncos, apneia percebida pelo parceiro(a). Sonolência diurna.
* **Estresse:** Nível de estresse (trabalho, pessoal). Como lida?
* **Humor:** Ansiedade, depressão (diagnóstico prévio? tratamento?).
* **Situação Socioeconômica:** Escolaridade. Rede de apoio social/familiar.
* **Adesão a tratamentos prévios.**

## 10. Revisão de Sistemas (foco em sintomas endocrinológicos não abordados)
* **Geral:** Fadiga, astenia, alterações de peso não intencionais, febre, calafrios, sudorese (noturna?), alterações de apetite.
* **Pele e Anexos:** Pele seca, oleosa, sudorese excessiva ou diminuída, acantose nigricans, hirsutismo, alopecia, unhas frágeis, estrias (cor?), hiperpigmentação, vitiligo, xantomas/xantelasmas.
* **Olhos:** Alterações visuais (diplopia, escotomas, hemianopsia), proptose, lacrimejamento, olho seco, fotofobia.
* **Ouvidos, Nariz, Garganta:** Rouquidão, disfagia, aumento do volume da língua.
* **Pescoço:** Dor, aumento de volume (bócio), nódulos palpáveis, disfagia, dispneia por compressão.
* **Cardiovascular:** Palpitações, taquicardia, bradicardia, dor torácica, dispneia aos esforços, edema de membros inferiores, ortostase.
* **Respiratório:** Dispneia, tosse, sintomas de apneia do sono (roncos altos, pausas respiratórias).
* **Gastrointestinal:** Náuseas, vômitos, diarreia, constipação, dor abdominal, polifagia, empachamento.
* **Geniturinário:** Poliúria, polidipsia, noctúria, disúria, hematúria, galactorreia, irregularidade menstrual, disfunção sexual, infertilidade.
* **Neurológico/Psiquiátrico:** Cefaleia, tonturas, síncope, tremores, parestesias, fraqueza muscular, cãibras, alterações de humor (irritabilidade, ansiedade, depressão), alterações cognitivas (memória, concentração), sonolência/insônia.
* **Músculo-esquelético:** Dores ósseas, artralgia, mialgia, fraturas de fragilidade, perda de altura, diminuição de força muscular.
* **Crescimento e Desenvolvimento (crianças/adolescentes):** Velocidade de crescimento, desenvolvimento puberal (Escala de Tanner).

## 11. Exame Físico
* **Geral:** Estado geral (bom, regular, ruim), nível de consciência, fácies típica (cushingoide, mixedematosa, acromegálica, hipertireoidiana), hálito.
* **Antropometria:**
  * Peso (kg), Altura (m), IMC (kg/m²).
  * Circunferência da cintura (cm – medida no ponto médio entre a última costela e a crista ilíaca).
  * Circunferência do quadril (cm – medida na maior protuberância glútea). Relação cintura-quadril.
  * (Em crianças/adolescentes: plotar peso, altura, IMC nas curvas de crescimento adequadas – OMS, CDC). Perímetro cefálico em lactentes.
* **Sinais Vitais:**
  * Pressão Arterial (mmHg – sentado, braço adequado, após repouso; considerar ortostática se indicado: deitado e após 1 e 3 min em pé).
  * Frequência Cardíaca (bpm).
  * Frequência Respiratória (rpm).
  * Temperatura (°C) – se queixa de febre ou distermia.
* **Pele e Fâneros:**
  * Cor (pálida, ictérica, cianótica, hiperpigmentada – áreas de dobras, cicatrizes, mucosas).
  * Hidratação, elasticidade, turgor. Textura (fina, áspera).
  * Lesões específicas: acantose nigricans (pescoço, axilas, dobras), estrias (localização, cor – violáceas, nacaradas), xantomas, xantelasmas, vitiligo, equimoses, petéquias.
  * Hirsutismo (distribuição e densidade dos pelos). Alopecia (padrão).
  * Unhas (alterações de cor, forma, espessura – onicólise, coiloníquia).
* **Olhos:**
  * Exoftalmia, retração palpebral, edema periorbitário, quemose, limitação da motilidade ocular extrínseca (Doença de Graves).
  * Acuidade visual (grosseira), campimetria por confrontação (suspeita de lesão hipofisária compressiva). Fundo de olho (se retinopatia diabética suspeita e disponível).
* **Ouvidos, Nariz, Garganta:** Rouquidão, disfagia, aumento do volume da língua.
* **Pescoço:**
  * Inspeção: Simetria, abaulamentos, cicatrizes, turgência jugular. Deglutição (movimentação de massas).
  * Palpação da Tireoide (preferencialmente por trás do paciente): Tamanho (normal, aumentada – bócio difuso/nodular), consistência (macia, firme, endurecida, elástica), superfície (lisa, irregular, nodular – descrever nódulos: tamanho em cm, localização, consistência, mobilidade, dor), frêmito.
  * Palpação de linfonodos cervicais.
* **Cardiovascular:**
  * Ausculta cardíaca: ritmo, bulhas (normo/hipo/hiperfonéticas), sopros.
  * Pulsos periféricos (simetria, amplitude).
* **Respiratório:** Ausculta pulmonar (murmúrio vesicular, ruídos adventícios).
* **Abdômen:** Inspeção (distensão, circulação colateral, estrias). Ausculta (ruídos hidroaéreos). Palpação (hepatomegalia, esplenomegalia, massas).
* **Extremidades:**
  * Edema (presença, localização, cacifo).
  * Temperatura (quentes, frias), perfusão capilar.
  * Deformidades articulares, baqueteamento digital.
  * **Exame dos Pés em Diabéticos:**
    * Inspeção: Cor, pele (seca, fissuras, calosidades, úlceras), deformidades (dedos em garra/martelo, Artropatia de Charcot).
    * Palpação dos pulsos pedioso e tibial posterior.
    * Avaliação da sensibilidade: Monofilamento de 10g (Semmes-Weinstein), vibratória (diapasão 128Hz), dolorosa, térmica. Reflexos aquileus.
* **Neurológico (direcionado):** Força muscular proximal, tremores, reflexos tendinosos profundos.
* **Avaliação Puberal (Escala de Tanner):** Desenvolvimento de mamas e pelos pubianos em meninas; genitais e pelos pubianos em meninos.
* **Sinais Específicos (se suspeita):**
  * Fácies cushingoide, giba dorsal, fraqueza muscular proximal (Síndrome de Cushing).
  * Prognatismo, aumento de mãos/pés, espessamento cutâneo (Acromegalia).
  * Sinais de Chvostek e Trousseau (Hipocalcemia).
  * Hiperpigmentação de mucosas e dobras (Doença de Addison).

## 12. Exames Complementares Trazidos pelo Paciente
* **Listar e analisar resultados de:**
  * Exames laboratoriais (glicemia de jejum, hemoglobina glicada, perfil lipídico, TSH, T4 livre, T3, anticorpos antitireoidianos, PTH, cálcio, fósforo, vitamina D, cortisol, ACTH, prolactina, LH, FSH, testosterona, estradiol, etc.).
  * Testes funcionais (teste de tolerância à glicose, teste de supressão com dexametasona, etc.).
  * Exames de imagem (ultrassonografia de tireoide/abdômen/pélvica, densitometria óssea, tomografia computadorizada, ressonância magnética de sela túrcica/adrenais, cintilografia).
  * Biópsias/PAAF de tireoide.

## 13. Hipótese Diagnóstica (HD)
* Diagnóstico(s) principal(is).
* Diagnósticos diferenciais.
* Avaliação de risco cardiovascular global (escores como Framingham, ASCVD, etc., se aplicável).
* Estadiamento de complicações (em diabetes, por exemplo).

## 14. Plano Terapêutico e Conduta
* **Metas Terapêuticas:** (Ex: HbA1c <7%, LDL <100mg/dL, PA <130/80mmHg, TSH dentro da referência, perda de X% do peso).
* **Tratamento Medicamentoso:**
  * Nome do(s) fármaco(s), dose, via, posologia, duração.
  * Orientações sobre como e quando tomar, possíveis efeitos colaterais, o que fazer se esquecer uma dose.
  * Ajuste de medicações em uso.
* **Tratamento Não Medicamentoso:**
  * **Orientação Alimentar:** Plano alimentar individualizado (ou encaminhamento para nutricionista). Metas calóricas, distribuição de macronutrientes, restrições específicas.
  * **Orientação de Atividade Física:** Tipo, frequência, duração, intensidade recomendada.
  * **Mudanças de Hábitos de Vida:** Cessação do tabagismo, moderação do álcool, higiene do sono.
* **Educação do Paciente:**
  * Sobre a(s) doença(s): fisiopatologia básica, cronicidade, importância do controle.
  * Automonitoramento (glicemia capilar, PA, peso, sintomas).
  * Prevenção e reconhecimento de complicações agudas (hipo/hiperglicemia, cetoacidose, crise adrenal) e crônicas.
  * Cuidados com os pés (em diabéticos).
* **Solicitação de Exames Complementares Adicionais:** (Especificar e justificar).
* **Encaminhamentos:** Nutricionista, educador físico, psicólogo, oftalmologista, nefrologista, cardiologista, cirurgião, podólogo, etc.
* **Agendamento de Retorno:** Data/prazo para reavaliação e monitoramento.
`,
  return: `Consulta de Retorno - Endocrinologia e Metabologia

## 1. Lista de Problemas e HDA Prévia da Consulta Anterior
* Resumo dos diagnósticos ativos.
* Metas terapêuticas estabelecidas.
* Plano terapêutico instituído (medicamentoso e não medicamentoso).

## S - Subjetivo
* **Motivo do Retorno:** (Agendado, reavaliação, resultados de exames, intercorrência, etc.).
* **Evolução dos Sintomas desde a Última Consulta:**
  * Melhora, piora ou estabilidade dos sintomas relatados anteriormente.
  * Surgimento de novos sintomas ou preocupações.
* **Adesão ao Tratamento:**
  * **Medicamentoso:**
    * Usou as medicações conforme prescrito? (Nome, dose, horários).
    * Ocorrência de esquecimento de doses? Quantas vezes?
    * Dificuldades para obter/comprar a medicação?
  * **Não Medicamentoso:**
    * Conseguiu seguir o plano alimentar? Dificuldades?
    * Realizou atividade física conforme orientado? Dificuldades?
    * Mudanças nos hábitos de vida (tabagismo, etilismo, sono)?
* **Automonitoramento (se aplicável):**
  * Glicemia capilar: Frequência das medidas, valores (trazer anotações/glicosímetro com memória).
  * Pressão arterial: Valores.
  * Peso corporal.
  * Diário de sintomas (se orientado).
* **Intercorrências desde a Última Consulta:**
  * Episódios de hipoglicemia (sintomas, como tratou, valor da glicemia se medido).
  * Episódios de hiperglicemia sintomática, cetoacidose, estado hiperosmolar.
  * Infecções, outras doenças agudas, internações, novas medicações prescritas por outros médicos.
  * Crise adrenal, tempestade tireotóxica (se aplicável ao caso).
* **Efeitos Colaterais das Medicações:**
  * Percebeu algum efeito adverso? Qual? Intensidade?
* **Impacto na Qualidade de Vida:** Mudanças desde a última avaliação.
* **Dúvidas do Paciente:** Anotar para esclarecimento.

## O - Objetivo
* **Antropometria:**
  * Peso atual (kg). Variação desde a última consulta.
  * IMC (kg/m²).
  * Circunferência da cintura (cm) – comparar.
* **Sinais Vitais:**
  * Pressão Arterial (mmHg).
  * Frequência Cardíaca (bpm).
* **Exame Físico Direcionado:**
  * Foco nas alterações relevantes para o(s) diagnóstico(s) e acompanhamento.
  * Ex: Palpação da tireoide, exame dos pés em diabéticos, avaliação de edema, pele, sinais de hiper/hipocortisolismo, etc.
  * Comparar com achados anteriores.
* **Resultados de Exames Complementares Solicitados na Consulta Anterior ou Trazidos:**
  * Analisar e interpretar exames laboratoriais (glicemia, HbA1c, perfil lipídico, função tireoidiana, renal, hepática, eletrólitos, hormônios específicos, etc.).
  * Resultados de exames de imagem (USG, densitometria, etc.).
  * Comparar com valores prévios e metas terapêuticas.

## A - Avaliação
* **Status Atual da(s) Doença(s) Endócrina(s)/Metabólica(s):**
  * Controlada, parcialmente controlada, não controlada.
* **Resposta à Terapia Instituída:**
  * Avaliar eficácia das medicações e mudanças no estilo de vida.
  * Atingimento das metas terapêuticas (glicêmicas, lipídicas, pressóricas, hormonais, ponderais, de sintomas, etc.).
  * Se metas não atingidas, analisar possíveis causas (adesão, dose inadequada, progressão da doença, diagnóstico, etc.).
* **Tolerabilidade do Tratamento:** Presença e manejo de efeitos colaterais.
* **Avaliação de Complicações Crônicas:** (Rastreamento/progressão de retinopatia, nefropatia, neuropatia, doença cardiovascular, etc.).
* **Avaliação de Risco:** (Cardiovascular, de fraturas, etc.).
* **Necessidade de Ajustes no Plano Terapêutico.**

## P - Plano
* **Ajustes no Tratamento Medicamentoso:**
  * Manter, aumentar/diminuir dose, trocar medicação, adicionar nova droga, suspender.
  * Justificar as alterações.
* **Reforço/Modificações no Tratamento Não Medicamentoso:**
  * Orientação alimentar e de atividade física (reforçar, ajustar metas).
  * Estratégias para melhorar a adesão.
* **Educação Continuada:**
  * Esclarecer dúvidas.
  * Reforçar conhecimentos sobre a doença, automonitoramento, prevenção de complicações.
* **Solicitação de Novos Exames Complementares:** (Para monitoramento, rastreamento de complicações, avaliação de controle).
* **Metas para o Próximo Período.**
* **Encaminhamentos:** (Manter ou novos encaminhamentos, se necessário).
* **Agendamento do Próximo Retorno:** Definir intervalo com base no controle da doença, risco de complicações e necessidade de monitoramento. Sinais de alerta para retorno antecipado.
`,
},

"Hematologia e Hemoterapia": {
  first: `Anamnese para Primeira Consulta - Hematologia e Hemoterapia

## 1. Identificação
* **Nome completo:**
* **Data de Nascimento:**
* **Idade:**
* **Sexo/Gênero:**
* **Estado Civil:**
* **Profissão (atual e anteriores, detalhar atividades):**
* **Naturalidade:**
* **Procedência:**
* **Endereço completo:**
* **Telefone(s) de contato:**
* **E-mail:**
* **Convênio/SUS:**
* **Quem indicou/Como chegou ao serviço (se por alteração em exame, qual exame e valor):**
* **Data da Consulta:**

## 2. Queixa Principal (QP)
* **Motivo principal da consulta (registrar com as palavras do paciente e duração):**
  * Ex: "Cansaço e palidez há 2 meses", "Sangramento fácil nas gengivas há 3 semanas", "Gânglios aumentados no pescoço há 1 mês", "Exame de sangue alterado (anemia/plaquetas baixas/leucócitos altos)".

## 3. História da Doença Atual (HDA)
* **Detalhar a(s) queixa(s) principal(is):**
  * **Início:** Quando e como os sintomas/alterações começaram? Início súbito ou gradual?
  * **Fator Desencadeante/Associado:** Infecção recente, novo medicamento, trauma, estresse, exposição a produtos químicos?
  * **Características dos Sintomas:**
    * **Síndrome Anêmica:** Fadiga, astenia, palidez cutaneomucosa, dispneia aos esforços, palpitações, tontura, cefaleia, sonolência, dificuldade de concentração, geofagia (desejo de comer terra/gelo).
    * **Fenômenos Hemorrágicos:**
      * **Pele/Mucosas:** Petéquias (pontos vermelhos), equimoses (manchas roxas – espontâneas ou desproporcionais ao trauma), hematomas. Gengivorragia, epistaxe (sangramento nasal – frequência, volume, espontâneo?), sangramento em sítios de punção.
      * **Trato Gastrointestinal:** Hematêmese (vômito com sangue), melena (fezes pretas), enterorragia/hematoquezia (sangue vivo nas fezes).
      * **Trato Geniturinário:** Hematuria (sangue na urina), menorragia (fluxo menstrual excessivo), metrorragia (sangramento uterino fora do período menstrual).
      * **Outros:** Hemoptise (tosse com sangue), hemartrose (sangramento articular), sangramento excessivo pós-procedimentos (dentários, cirúrgicos).
    * **Fenômenos Trombóticos:** Dor, edema, rubor, calor em membros (TVP?), dor torácica, dispneia súbita (TEP?), déficits neurológicos súbitos (AVC?). Histórico pessoal de trombose prévia.
    * **Sintomas Constitucionais (Sintomas B):** Febre (vespertina? aferida? duração?), sudorese noturna (molhar lençóis?), perda de peso inexplicada (>10% do peso corporal em 6 meses?).
    * **Linfonodomegalia:** Localização (cervical, axilar, inguinal, etc.), tamanho percebido, consistência, dor, tempo de surgimento, crescimento rápido?
    * **Hepatoesplenomegalia:** Dor ou desconforto abdominal, saciedade precoce.
    * **Dores Ósseas:** Localização, intensidade, tipo (contínua, intermitente), piora à noite ou com movimento.
    * **Sintomas Infecciosos:** Infecções de repetição (tipo, frequência, gravidade, necessidade de internação).
    * **Outros:** Prurido (especialmente após banho quente – policitemia vera?), alterações neurológicas (parestesias, fraqueza, alterações visuais, cefaleia), icterícia.
  * **Evolução dos Sintomas:** Progressivos, intermitentes, estáveis?
  * **Tratamentos Anteriores (para esta queixa):** Medicações (ferro, vitaminas, corticoides, etc.), transfusões. Resultados?
  * **Investigações Prévias:** Hemogramas anteriores, mielograma, biópsia de medula óssea, exames de imagem. Trazer resultados.

## 4. Antecedentes Pessoais Patológicos (APP)
* **Doenças Hematológicas Previamente Diagnosticadas:**
  * Anemias (ferropriva, megaloblástica, falciforme, talassemia, aplásica, hemolítica autoimune).
  * Distúrbios da Coagulação (hemofilias, Doença de von Willebrand, trombocitopenias imunes - PTI).
  * Trombofilias (hereditárias ou adquiridas).
  * Neoplasias Hematológicas (leucemias agudas/crônicas, linfomas Hodgkin/Não-Hodgkin, mieloma múltiplo, síndromes mielodisplásicas, neoplasias mieloproliferativas – Policitemia Vera, Trombocitemia Essencial, Mielofibrose).
  * Hemoglobinopatias.
* **Outras Doenças Crônicas:**
  * Doenças autoimunes (LES, artrite reumatoide, SAAF).
  * Doenças renais crônicas.
  * Doenças hepáticas crônicas (cirrose, hepatites).
  * Doenças inflamatórias intestinais.
  * Cardiopatias.
  * Infecções crônicas (HIV, Hepatites B/C, tuberculose).
  * Câncer sólido prévio (tratamentos realizados).
* **Cirurgias Prévias:** Especialmente esplenectomia, gastrectomia, ressecções intestinais, linfadenectomias, timectomia. Cirurgias com sangramento excessivo.
* **Transfusões Sanguíneas:**
  * Já recebeu? (Concentrado de hemácias, plaquetas, plasma, crioprecipitado).
  * Motivo, data(s), número de unidades.
  * Reações transfusionais (tipo de reação).
  * Aloimunização.
* **Transplante de Medula Óssea (TMO) / Células Tronco Hematopoiéticas (TCTH):** Autólogo ou alogênico? Data, motivo, complicações (DECH – Doença do Enxerto Contra Hospedeiro).
* **Quimioterapia, Radioterapia, Imunoterapia Prévias:** Quais drogas/regiões? Datas.
* **Alergias:** Medicamentos, alimentos, contraste.
* **Medicações em Uso Contínuo:** Nome, dose, posologia, motivo. (Incluir anticoagulantes, antiagregantes plaquetários, AAS, AINEs, ferro, ácido fólico, vitamina B12, corticoides, imunossupressores, quimioterápicos orais, suplementos, fitoterápicos).
* **Imunizações:** Histórico vacinal (especialmente para pacientes imunossuprimidos ou esplenectomizados – Pneumococo, Meningococo, Haemophilus influenzae tipo B, Influenza).

## 5. Antecedentes Ginecológicos e Obstétricos (para mulheres)
* **Menarca:** Idade.
* **Ciclos Menstruais:** Duração, intervalo, regularidade.
* **Menorragia/Metrorragia:** Fluxo menstrual (número de absorventes/dia, presença de coágulos). Sangramento intermenstrual.
* **Uso de Anticoncepcionais Hormonais ou DIU.**
* **Gestação(ões):** Número (G), Partos (P), Abortos (A).
  * Anemia na gestação? Necessidade de ferroterapia ou transfusão?
  * Sangramento excessivo no parto ou pós-parto?
  * Eventos trombóticos durante a gestação ou puerpério?
  * Filhos com icterícia neonatal prolongada ou anemia?

## 6. Antecedentes Familiares
* **Pais, irmãos, filhos (idade, estado de saúde, causa de óbito):**
* **Histórico Familiar de Doenças Hematológicas:**
  * Anemias hereditárias (falciforme, talassemias, esferocitose).
  * Distúrbios de coagulação (hemofilias, Doença de von Willebrand).
  * Trombofilias.
  * Neoplasias hematológicas (leucemias, linfomas, mieloma).
  * Icterícia crônica, cálculos biliares em idade jovem (sugestivo de hemólise crônica).
  * Esplenectomia em familiares.
* **Consanguinidade entre os pais.**
* **Origem Étnica:** (Relevante para hemoglobinopatias, deficiência de G6PD).

## 7. História Ocupacional e Exposicional
* **Profissão(ões):** Exposição a radiação ionizante, benzeno e outros solventes orgânicos, pesticidas, herbicidas, inseticidas, metais pesados (chumbo, arsênico).
* **Hobbies com exposição a produtos químicos.**
* **Moradia em áreas rurais ou com saneamento precário (parasitoses).**
* **Viagens Recentes:** Áreas endêmicas para malária, leishmaniose, outras infecções.
* **Contato com animais (doenças zoonóticas).**

## 8. Hábitos de Vida
* **Alimentação:**
  * Dieta habitual (consumo de carnes vermelhas, vegetais verde-escuros, frutas cítricas).
  * Dietas restritivas (vegetarianismo, veganismo – atenção à B12, ferro).
  * Pica (geofagia, pagofagia).
* **Tabagismo:** Sim/Não. Carga tabágica.
* **Etilismo:** Sim/Não. Tipo, frequência, quantidade (risco de deficiência de folato, hepatopatia).
* **Uso de Drogas Ilícitas:** (Especialmente injetáveis – risco de infecções virais).
* **Atividade Física:** Tipo, frequência.
* **Qualidade do Sono.**

## 9. Revisão de Sistemas (foco em sintomas hematológicos e constitucionais não abordados)
* **Geral:** Fadiga, astenia, mal-estar, perda de peso, febre, calafrios, sudorese (noturna?).
* **Pele e Mucosas:** Palidez, icterícia, cianose, petéquias, púrpuras, equimoses, hematomas, prurido, eritema facial (pletora), lesões cutâneas infiltrativas, úlceras (especialmente em membros inferiores), glossite, queilite angular.
* **Linfonodos:** Dor ou aumento percebido em qualquer cadeia.
* **Cabeça e Pescoço:** Cefaleia, tontura, zumbido, alterações visuais, sangramento gengival, dor de garganta, disfagia.
* **Cardiorrespiratório:** Dispneia (repouso, esforços), ortopneia, DPN, palpitações, dor torácica, tosse, hemoptise.
* **Gastrointestinal:** Dor abdominal, náuseas, vômitos, hematêmese, melena, hematoquezia, alteração do hábito intestinal, saciedade precoce, distensão abdominal (ascite?).
* **Geniturinário:** Hematuria, menorragia, metrorragia, disúria, urgência miccional, priapismo.
* **Músculo-esquelético:** Dores ósseas (difusas ou localizadas), artralgia, edema articular, mialgia, claudicação.
* **Neurológico:** Parestesias, fraqueza muscular, alterações de marcha/equilíbrio, confusão mental, sonolência, alterações de comportamento, síncope, convulsões, déficits focais.
* **Vascular:** Fenômenos de Raynaud, eritromelalgia.

## 10. Exame Físico
* **Geral:**
  * Estado geral (bom, regular, ruim), nível de consciência, fácies típica.
  * Performance Status (ECOG/Karnofsky).
  * Estado nutricional (IMC, sinais de desnutrição ou obesidade).
* **Sinais Vitais:**
  * Pressão Arterial (mmHg), Frequência Cardíaca (bpm), Frequência Respiratória (rpm), Temperatura (°C), SatO₂ (%).
* **Pele e Mucosas:**
  * Cor: Palidez (avaliar conjuntivas palpebrais, leito ungueal, palma das mãos), icterícia (escleras, pele), cianose, pletora facial.
  * Hemorragias: Petéquias (distribuição), púrpuras, equimoses (tamanho, localização, recentes/antigas), hematomas. Sufusões hemorrágicas.
  * Lesões específicas: Úlceras (especialmente maleolares), lesões infiltrativas, rash.
  * Turgor, hidratação.
  * Unhas: Coiloníquia (unhas em colher – ferropenia), baqueteamento digital, palidez do leito ungueal, hemorragias subungueais.
  * Cabelos: Quebradiços, secos, alopecia.
* **Linfonodos:** Palpação sistemática de todas as cadeias acessíveis:
  * Cervicais (anterior, posterior, submandibular, submentoniana, supraclavicular).
  * Axilares.
  * Epitrocleares.
  * Inguinais (superficiais e profundos).
  * Poplíteos (se indicado).
  * Descrever: localização, número, tamanho (em cm), consistência (móvel, endurecido, elástico, pétreo, amolecido), mobilidade (móveis ou aderidos a planos profundos), coalescência, sensibilidade (dolorosos ou indolores), sinais flogísticos.
* **Boca e Orofaringe:**
  * Mucosa oral: palidez, petéquias, úlceras, sangramentos.
  * Gengivas: hipertrofia, sangramento espontâneo ou ao toque.
  * Língua: lisa, despapilada (glossite – def. ferro/B12/folato), macroglossia.
  * Orofaringe: hipertrofia de amígdalas, petéquias em palato.
* **Pescoço:** Tireoide (aumento, nódulos), turgência jugular.
* **Tórax e Pulmões:**
  * Inspeção: Simetria, abaulamentos, retrações, uso de musculatura acessória.
  * Palpação: Frêmito tóraco-vocal, expansibilidade.
  * Percussão: Macicez, submacicez, timpanismo.
  * Ausculta: Murmúrio vesicular, ruídos adventícios (estertores, sibilos, atritos).
* **Cardiovascular:**
  * Inspeção e palpação do precórdio (ictus cordis).
  * Ausculta: Ritmo, bulhas (normo/hipo/hiperfonéticas), sopros (sopro sistólico de hiperfluxo na anemia).
* **Abdômen:**
  * Inspeção: Forma (plano, globoso, escavado, distendido), cicatrizes, circulação colateral, equimoses.
  * Ausculta: Ruídos hidroaéreos.
  * Percussão: Timpanismo, macicez (hepática, esplênica, ascítica – sinal do piparote, macicez móvel).
  * Palpação:
    * Superficial: sensibilidade, massas.
    * Profunda:
      * Fígado: Hepatimetria (cm abaixo do rebordo costal direito na linha hemiclavicular – RCD LHC), consistência (macia, firme, endurecida), superfície (lisa, nodular), borda (fina, romba), sensibilidade.
      * Baço: Palpável ou não (normalmente não é palpável). Se palpável, esplenomegalia (cm abaixo do rebordo costal esquerdo – RCE), consistência, sensibilidade. (Manobra de Schuster, Mathieu-Cardarelli).
      * Rins (manobra de Giordano, se suspeita).
      * Outras massas abdominais, linfonodos retroperitoneais (se palpáveis).
* **Músculo-esquelético:**
  * Dor à palpação/percussão óssea (esterno, tíbias, coluna vertebral, gradil costal).
  * Edema, rubor, calor, limitação de movimento articular (suspeita de hemartrose ou artrite).
* **Neurológico (direcionado):**
  * Nível de consciência, orientação.
  * Pares cranianos (se sintomas específicos).
  * Força muscular, sensibilidade (vibratória, tátil, dolorosa – especialmente em membros inferiores para neuropatia periférica), coordenação, marcha, equilíbrio.
  * Reflexos tendinosos profundos.
  * Sinais meníngeos (se suspeita de infiltração liquórica ou infecção do SNC).

## 11. Exames Complementares Trazidos pelo Paciente
* **Listar e analisar resultados de:**
  * Hemograma completo com contagem diferencial de leucócitos e plaquetas (analisar todos os parâmetros, incluindo índices hematimétricos – VCM, HCM, CHCM, RDW).
  * Contagem de reticulócitos.
  * Esfregaço de sangue periférico (descrição morfológica das células).
  * Coagulograma (TAP/INR, TTPA, Tempo de Trombina, Fibrinogênio, Dímero-D).
  * Dosagem de fatores da coagulação, pesquisa de anticoagulante lúpico, outros testes de trombofilia.
  * Eletroforese de hemoglobina, teste de falcização.
  * Dosagens: Ferro sérico, ferritina, saturação de transferrina, capacidade total de ligação do ferro (TIBC), Vitamina B12, Ácido Fólico.
  * Teste de Coombs direto e indireto.
  * Bilirrubinas (total, direta, indireta), LDH, Haptoglobina.
  * Função renal (ureia, creatinina), Função hepática (AST, ALT, FA, GGT).
  * Ácido úrico, Proteínas totais e frações (eletroforese de proteínas séricas e urinárias, imunofixação).
  * Beta-2 microglobulina.
  * Sorologias virais (HIV, HBV, HCV, EBV, CMV).
  * Mielograma (descrição citológica da medula óssea).
  * Biópsia de medula óssea (histopatológico, imuno-histoquímica).
  * Citogenética/FISH, Biologia Molecular (pesquisa de mutações).
  * Exames de imagem (Radiografias, Ultrassonografias, Tomografias Computadorizadas – TC, Ressonância Magnética – RM, PET-CT).

## 12. Hipótese Diagnóstica (HD)
* Diagnóstico(s) principal(is) (ex: Anemia ferropriva, Leucemia Mieloide Aguda, Linfoma de Hodgkin, PTI, etc.).
* Diagnósticos diferenciais.
* Classificação/Estadiamento da doença (se aplicável).
* Avaliação de prognóstico (se possível).

## 13. Plano Terapêutico e Conduta
* **Tratamento Específico (se diagnóstico definido):**
  * Quimioterapia (protocolo, drogas, doses, ciclos).
  * Imunoterapia, Terapia alvo.
  * Radioterapia.
  * Cirurgia (esplenectomia, etc.).
  * Transplante de Medula Óssea/Células Tronco Hematopoiéticas (indicação).
  * Ferroterapia (oral, venosa), reposição de vitaminas.
  * Anticoagulação/Antiagregação.
  * Imunossupressão.
* **Tratamento de Suporte:**
  * Transfusões (hemácias, plaquetas, plasma, etc. – indicar limiares).
  * Fatores de crescimento hematopoiético (eritropoetina, G-CSF).
  * Profilaxia/Tratamento de infecções.
  * Manejo da dor.
  * Suporte nutricional.
  * Suporte psicológico.
* **Orientações Gerais:**
  * Sobre a doença, tratamento, possíveis efeitos colaterais.
  * Sinais de alerta para procurar serviço de emergência (febre em neutropênico, sangramentos importantes, etc.).
  * Cuidados com higiene, alimentação.
  * Restrições (se houver).
  * **Solicitação de Exames Complementares Adicionais:** (Especificar e justificar – para confirmação diagnóstica, estadiamento, avaliação pré-tratamento).
* **Encaminhamentos:** Outros especialistas (infectologista, oncologista clínico, cirurgião, dentista para avaliação pré-QT, etc.), nutricionista, psicólogo, assistente social.
* **Agendamento de Retorno:** Data/prazo para resultados de exames, início de tratamento, reavaliação.
`,
  return: `Consulta de Retorno - Hematologia e Hemoterapia

## 1. Lista de Problemas e HDA Prévia da Consulta Anterior
* Diagnóstico(s) hematológico(s) estabelecido(s).
* Estadiamento/Classificação da doença.
* Plano terapêutico instituído (quimioterapia, imunoterapia, suporte transfusional, etc.).
* Complicações ou intercorrências desde a última avaliação.

## S - Subjetivo
* **Motivo do Retorno:** (Agendado para ciclo de QT, reavaliação pós-QT, acompanhamento, resultados de exames, intercorrência).
* **Evolução dos Sintomas Hematológicos desde a Última Consulta:**
  * Melhora, piora ou estabilidade dos sintomas (fadiga, sangramento, dor, febre, etc.).
  * Surgimento de novos sintomas.
* **Resposta ao Tratamento Instituído:**
  * Percepção do paciente sobre a eficácia do tratamento.
* **Adesão ao Tratamento:**
  * **Medicamentoso (oral, subcutâneo):** Usou conforme prescrito? Dificuldades? Esquecimentos?
  * **Comparecimento a sessões de QT/RT, transfusões, consultas.**
* **Tolerância ao Tratamento / Efeitos Colaterais:**
  * Náuseas, vômitos, diarreia, constipação.
  * Mucosite (dor, aftas).
  * Fadiga, fraqueza.
  * Alopecia.
  * Neuropatia periférica (formigamentos, dor).
  * Rash cutâneo.
  * Mialgia, artralgia.
  * Sintomas de infecção (febre, calafrios, tosse, disúria).
  * Como os efeitos colaterais foram manejados?
* **Intercorrências desde a Última Consulta:**
  * Febre (valor máximo, duração, procurou atendimento?).
  * Sangramentos (local, volume).
  * Infecções (tipo, tratamento).
  * Necessidade de internação hospitalar.
  * Eventos trombóticos.
  * Reações transfusionais.
* **Suporte Transfusional:** Precisou de transfusão de hemácias ou plaquetas desde a última consulta? Quantas unidades?
* **Impacto na Qualidade de Vida e Atividades Diárias:** (Melhora, piora, limitações). Performance Status.
* **Estado Psicológico/Emocional.**
* **Dúvidas do Paciente.**

## O - Objetivo
* **Sinais Vitais:** PA, FC, FR, Temp, SatO₂.
* **Peso Corporal:** (Variação desde a última consulta – importante em QT).
* **Exame Físico Direcionado:**
  * Avaliar mucosas (palidez, icterícia, petéquias, mucosite).
  * Palpar linfonodos (comparar com exame anterior).
  * Ausculta cardíaca e pulmonar.
  * Palpação abdominal (fígado, baço – comparar tamanho).
  * Inspecionar pele (rash, petéquias, equimoses, sítios de punção).
  * Avaliar sinais de infecção ou sangramento.
  * Avaliação neurológica (se neuropatia induzida por QT).
* **Resultados de Exames Complementares Solicitados ou de Rotina:**
  * Hemograma completo (comparar com anteriores – avaliar nadir, recuperação medular).
  * Coagulograma.
  * Bioquímica (função renal, hepática, eletrólitos, LDH, ácido úrico – avaliar toxicidade, síndrome de lise tumoral).
  * Marcadores tumorais (se aplicável).
  * Mielograma / Biópsia de Medula Óssea de controle (se realizado).
  * Exames de imagem de reestadiamento (TC, PET-CT).

## A - Avaliação
* **Status Atual da Doença Hematológica:**
  * **Resposta ao tratamento:**
    * Remissão Completa (RC), Remissão Parcial (RP), Doença Estável (DE), Progressão de Doença (PD) – (usar critérios específicos para cada doença, ex: ELN para LMA, Cheson para Linfomas).
    * Resposta citogenética/molecular (se aplicável).
  * Doença residual mínima (DRM).
* **Avaliação da Toxicidade do Tratamento:**
  * Graduar efeitos colaterais (ex: CTCAE).
  * Hematológica (neutropenia, plaquetopenia, anemia).
  * Não hematológica (renal, hepática, neurológica, gastrointestinal, cardíaca).
* **Hematopoiese:** Avaliação da recuperação medular pós-QT.
* **Manejo de Complicações:** (Infecções, sangramentos, toxicidades).
* **Necessidade de Suporte Contínuo:** (Transfusões, fatores de crescimento, profilaxia antimicrobiana).
* **Prognóstico Atualizado.**

## P - Plano
* **Tratamento Específico:**
  * Continuar protocolo atual.
  * Próximo ciclo de QT/Imunoterapia (data, drogas, doses).
  * Ajuste de doses devido à toxicidade ou resposta.
  * Mudança de linha terapêutica (se progressão ou intolerância).
  * Considerar TMO/TCTH (se indicado e não previamente discutido/programado).
  * Terapia de manutenção.
* **Manejo de Efeitos Colaterais e Toxicidades:**
  * Prescrição de antieméticos, analgésicos, protetores gástricos, tratamento para mucosite, etc.
  * Orientações para manejo domiciliar.
* **Suporte Hematológico e Clínico:**
  * Programação de transfusões.
  * Uso de fatores de crescimento.
  * Profilaxia/tratamento de infecções (antibióticos, antifúngicos, antivirais).
  * Hidratação venosa, correção de distúrbios eletrolíticos.
* **Orientações ao Paciente e Familiares:**
  * Sobre a fase atual do tratamento, próximos passos.
  * Reconhecimento de sinais de alerta.
* **Solicitação de Novos Exames Complementares:** (Para monitoramento da doença, toxicidade, antes do próximo ciclo).
* **Encaminhamentos:** Outros especialistas (infectologista, oncologista clínico, cirurgião, dentista para avaliação pré-QT, etc.), nutricionista, psicólogo, assistente social.
* **Agendamento de Retorno:** Data/prazo para resultados de exames, início de tratamento, reavaliação.
`,
},

"Pneumologia": {
  first: `Anamnese para Primeira Consulta - Pneumologia

## 1. Identificação
* **Nome completo:**
* **Data de Nascimento:**
* **Idade:**
* **Sexo/Gênero:**
* **Estado Civil:**
* **Profissão (atual e anteriores, detalhar tipo de trabalho, ambiente e exposições):**
* **Naturalidade:**
* **Procedência:**
* **Endereço completo:**
* **Telefone(s) de contato:**
* **E-mail:**
* **Convênio/SUS:**
* **Quem indicou/Como chegou ao serviço:**
* **Data da Consulta:**

## 2. Queixa Principal (QP)
* **Motivo principal da consulta (registrar com as palavras do paciente e duração):**
  * Ex: "Falta de ar ao caminhar há 6 meses", "Tosse com catarro amarelo há 3 semanas", "Chiado no peito desde a infância que piorou".

## 3. História da Doença Atual (HDA)
* **Detalhar a(s) queixa(s) principal(is):**
  * **Dispneia:**
    * **Início:** Súbito ou gradual? Em repouso ou aos esforços?
    * **Intensidade:** Escalas (ex: mMRC 0-4, Borg). Limitação das atividades diárias (quantificar, ex: subir quantos lances de escada? Caminhar quantos metros?).
    * **Fatores desencadeantes ou de piora:** Esforço, decúbito (ortopneia – quantos travesseiros usa?), exposição a alérgenos/irritantes, infecções, mudanças climáticas, ansiedade.
    * **Fatores de alívio:** Repouso, medicação (qual? resposta?), posição específica.
    * **Evolução:** Progressiva, estável, intermitente?
    * Dispneia Paroxística Noturna (DPN)?
  * **Tosse:**
    * **Início e duração.**
    * **Características:** Seca ou produtiva? Cor e aspecto da secreção? Período do dia em que piora? Dispneia associada? Chiado?
    * **Se produtiva (escarro):** Volume (colher de chá, sopa, copo), cor (claro, amarelado, esverdeado, acastanhado, sanguinolento), consistência (fluido, espesso, mucoso, purulento), odor (fétido?). Hemoptise (sangue vivo, raias de sangue, sangue escuro – diferenciar de epistaxe e hematêmese)?
    * **Fatores desencadeantes ou de piora:** Mudança de decúbito, fala, riso, esforço, exposição a poeira/fumaça/cheiros fortes, frio, noite/manhã.
    * **Fatores de alívio:** Medicações (quais?), hidratação.
  * **Sibilância (Chiado no Peito):**
    * **Início, frequência, duração dos episódios.**
    * **Percepção (pelo paciente ou outros). Audível à distância?**
    * **Fatores desencadeantes ou de piora:** Esforço, alérgenos, irritantes, infecções, riso, choro.
    * **Fatores de alívio:** Medicação broncodilatadora (qual? resposta? frequência de uso de resgate?).
    * **Sintomas associados durante as crises (tosse, dispneia, opressão torácica).**
  * **Dor Torácica:**
    * **Localização, irradiação, tipo (pontada, queimação, peso, facada), intensidade (0-10).**
    * **Relação com respiração (pleurítica – piora com inspiração profunda, tosse), esforço, posição.**
    * **Duração, frequência. Fatores de alívio/piora.**
  * **Outros Sintomas Respiratórios:** Rouquidão, estridor, cornagem, vômica.
  * **Sintomas Sistêmicos Associados:** Febre (aferida? valor? duração?), calafrios, sudorese noturna, perda de peso inexplicada, fadiga, astenia, anorexia.
  * **Tratamentos Anteriores para a Queixa Atual:** Medicações (nome, dose, duração, resposta), nebulizações, oxigenoterapia.
  * **Procurou outros serviços/Internações pela queixa atual? Diagnósticos? Tratamentos?**
  * **Impacto da Doença:** Nas atividades de vida diária, trabalho, lazer, sono, humor.

## 4. Antecedentes Pessoais Patológicos (APP)
* **Doenças Respiratórias Prévias:**
  * Asma na infância/adulto (diagnóstico, gravidade, tratamento, última crise, necessidade de corticoide oral/internação).
  * Bronquite crônica, Enfisema Pulmonar (DPOC) (diagnóstico, tratamento, exacerbações).
  * Pneumonias (quantos episódios, quando, onde tratou – domicílio/hospital, complicações – derrame pleural, abscesso).
  * Tuberculose Pulmonar ou Extrapulmonar (data do diagnóstico, tratamento realizado – esquema, duração, se completou, cura, sequelas). Contato com tuberculose.
  * Bronquiectasias. Fibrose Cística. Doenças Pulmonares Intersticiais (DPI) (sarcoidose, fibrose pulmonar idiopática, pneumonite de hipersensibilidade).
  * Tromboembolismo Pulmonar (TEP) prévio.
  * Derrame Pleural (causa, tratamento). Pneumotórax.
  * Câncer de Pulmão ou outro câncer com metástase pulmonar.
  * Apneia Obstrutiva do Sono (SAOS) (diagnóstico por polissonografia? uso de CPAP?). Roncos, sonolência diurna.
* **História de Atopia:** Rinite alérgica, sinusite crônica, dermatite atópica, conjuntivite alérgica, alergia alimentar ou medicamentosa (quais? tipo de reação?).
* **Doenças Cardiovasculares:** Hipertensão arterial, insuficiência cardíaca, doença isquêmica do coração, arritmias (podem cursar com dispneia).
* **Doença do Refluxo Gastroesofágico (DRGE):** (Pode causar tosse crônica, broncoespasmo).
* **Doenças Reumatológicas/Autoimunes:** (LES, artrite reumatoide, esclerodermia, vasculites – podem ter acometimento pulmonar).
* **Imunodeficiências:** Primárias ou adquiridas (HIV – CD4, carga viral; uso de imunossupressores).
* **Cirurgias Torácicas Prévias:** (Lobectomia, pneumectomia, biópsias pulmonares, toracoscopia, drenagem pleural). Outras cirurgias relevantes.
* **Traumatismos Torácicos.**
* **Internações Hospitalares Prévias por Problemas Respiratórios ou Outros.**
* **Alergias a Medicamentos:** Especificar droga e tipo de reação.
* **Medicações em Uso Contínuo:**
  * Nome, dose, posologia, via de administração, motivo do uso.
  * Inaladores: Nome, tipo de dispositivo (spray, pó seco, névoa suave), dose, frequência. CHECAR TÉCNICA INALATÓRIA. Uso de espaçador?
  * Oxigenoterapia domiciliar: Fonte (cilindro, concentrador), fluxo (L/min), duração (horas/dia – contínuo, noturno, aos esforços?).
  * Nebulizadores.
  * Corticoides orais ou inalatórios, broncodilatadores, antibióticos, diuréticos, anti-hipertensivos, anticoagulantes, imunossupressores, AINEs, beta-bloqueadores (podem piorar asma).

## 5. História de Tabagismo
* **Status Tabágico:** Fumante atual, ex-fumante, nunca fumou.
* **Se Fumante Atual ou Ex-Fumante:**
  * Idade que começou a fumar.
  * Tipo de tabaco: Cigarro industrializado, cigarro de palha/artesanal, charuto, cachimbo, narguilé, maconha.
  * Quantidade: Número de cigarros/dia ou maços/dia. Para outras formas, estimar frequência e quantidade.
  * Tempo de tabagismo (anos).
  * **Carga Tabágica:** (nº de cigarros/dia ÷ 20) x nº de anos que fumou = anos-maço.
  * Tentativas prévias de cessação: Quantas? Métodos utilizados? Tempo máximo de abstinência? Motivo da recaída?
  * Interesse em parar de fumar? (Grau de motivação).
* **Se Ex-Fumante:** Há quanto tempo parou?
* **Tabagismo Passivo:** Exposição em casa (cônjuge, pais), no trabalho, na infância.
* **Uso de Cigarros Eletrônicos/Vape/Dispositivos Eletrônicos para Fumar (DEFs):** Tipo, frequência, tempo de uso, substâncias utilizadas (nicotina, com sabor, THC?).

## 6. História Ocupacional e Ambiental
* **Profissão Atual e Anteriores Detalhadas:**
  * Descrição das atividades exercidas.
  * Tempo de exposição em cada função.
  * Exposição a: Poeiras (sílica, asbesto/amianto, carvão mineral, algodão, grãos, madeira, feno – mofo), fumos metálicos (soldagem), gases irritantes (amônia, cloro, dióxido de enxofre), vapores químicos, solventes orgânicos, isocianatos, névoas ácidas.
  * Uso de Equipamentos de Proteção Individual (EPI) – máscara (tipo), ventilação do ambiente.
* **Ambiente Doméstico:**
  * Tipo de moradia (casa, apartamento). Condições de ventilação e umidade.
  * Presença de mofo, poeira, ácaros.
  * Tipo de fogão (a lenha, a gás – ventilação?). Aquecimento ambiental.
  * Animais de estimação (cães, gatos, pássaros – contato com penas/pelos).
  * Exposição a fumaça de biomassa (fogão a lenha, queimadas).
  * Ar condicionado (limpeza de filtros).
  * Plantas dentro de casa.
* **Hobbies:** Marcenaria, pintura, cerâmica, jardinagem, criação de pássaros.
* **Viagens Recentes ou Antigas para Áreas Endêmicas:** Tuberculose, histoplasmose, paracoccidioidomicose, malária com complicações pulmonares.

## 7. Antecedentes Familiares
* **Saúde dos pais, irmãos, filhos (idade, estado de saúde, causa de óbito).**
* **Histórico Familiar de Doenças Respiratórias:**
  * Asma, DPOC, enfisema.
  * Fibrose Cística.
  * Deficiência de Alfa-1 Antitripsina.
  * Câncer de Pulmão.
  * Tuberculose.
  * Doenças pulmonares intersticiais familiares.
* **Histórico Familiar de Atopia/Alergias.**

## 8. Imunizações (Vacinação)
* **Vacina da Gripe (Influenza):** Anual? Data da última dose.
* **Vacinas Pneumocócicas:** VPC13 (conjugada), VPP23 (polissacarídica) – quais tomou? Datas.
* **Vacina COVID-19:** Quantas doses? Quais vacinas? Datas.
* **dTpa (Coqueluche).**
* **BCG (na infância – cicatriz?).**
* **Outras vacinas relevantes (Herpes Zoster em idosos/imunocomprometidos).**

## 9. Revisão de Sistemas (foco em sintomas associados e manifestações extrapulmonares)
* **Geral:** Febre, calafrios, sudorese (noturna?), perda ou ganho de peso inexplicado, fadiga, astenia, anorexia, caquexia.
* **Otorrinolaringológico (ORL):** Rinorreia (coriza – hialina, purulenta), obstrução nasal, espirros, prurido nasal/ocular, dor facial (sinusite), gotejamento pós-nasal, dor de garganta, rouquidão, disfonia, epistaxe.
* **Cardiovascular:** Dor torácica (anginosa?), palpitações, edema de membros inferiores, síncope, turgência jugular.
* **Gastrointestinal:** Azia, regurgitação, pirose, disfagia, odinofagia, náuseas, vômitos.
* **Músculo-esquelético:** Artralgia, artrite, mialgia, fraqueza muscular, baqueteamento digital, osteoartropatia hipertrófica.
* **Neurológico:** Cefaleia (matinal em SAOS), sonolência diurna excessiva, dificuldade de concentração, alterações de humor, tontura, parestesias.
* **Pele:** Cianose (central, periférica), rash cutâneo, eritema nodoso, lesões específicas (sarcoidose, vasculites).

## 10. Impacto Socioeconômico e Qualidade de Vida
* **Limitações nas atividades de vida diária (AVDs) e instrumentais (AIVDs).**
* **Impacto no trabalho (absenteísmo, incapacidade laboral).**
* **Impacto nas atividades de lazer e sociais.**
* **Alterações de humor (ansiedade, depressão) relacionadas à doença respiratória.**
* **Dependência de oxigênio ou outras terapias.**
* **Qualidade do sono.**
* **Uso de questionários de qualidade de vida (ex: CAT para DPOC, AQLQ para Asma), se aplicável.**

## 11. Exame Físico
* **Geral:**
  * Estado geral (bom, regular, ruim), nível de consciência, orientação.
  * Fácies (dispneica, pletórica, cianótica, cushingoide por corticoide).
  * Biotipo. Estado nutricional (IMC). Caquexia? Obesidade?
  * Postura (ortopneia, uso de musculatura acessória em repouso – tiragem supraclavicular, intercostal, fúrcula; respiração com lábios semicerrados/franzidos).
  * Fala (dispneia ao falar – frases curtas, entrecortadas).
* **Sinais Vitais:**
  * Pressão Arterial (PA) (mmHg).
  * Frequência Cardíaca (FC) (bpm). Pulso paradoxal?
  * Frequência Respiratória (FR) (irpm) – observar padrão: taquipneia, bradipneia, Kussmaul, Cheyne-Stokes, Biot. Ritmo e amplitude.
  * Temperatura Axilar (Tax) (°C).
  * Saturação de Oxigênio (SpO₂) em ar ambiente. Se baixa, repetir com oxigênio. Considerar aferir durante caminhada de 6 minutos, se indicado.
* **Antropometria:** Peso (kg), Altura (m), IMC (kg/m²). Circunferência do pescoço (SAOS).
* **Mãos e Unhas:**
  * Cianose de extremidades. Baqueteamento digital (dedos em vidro de relógio/baqueta de tambor).
  * Manchas de nicotina nos dedos. Asterixis/flapping (em hipercapnia severa).
* **Cabeça e Pescoço:**
  * Nariz: Deformidades, permeabilidade nasal, secreção, pólipos nasais, hipertrofia de cornetos.
  * Orofaringe: Hiperemia, exsudato, gotejamento pós-nasal, úvula, palato. Higiene oral (uso crônico de inaladores).
  * Pescoço: Posição da traqueia (central ou desviada). Estase jugular (turgência jugular patológica). Uso de musculatura acessória (esternocleidomastoideo, escalenos). Palpação de linfonodos cervicais e supraclaviculares. Palpação da tireoide.
* **Tórax:**
  * **Inspeção Estática:** Forma do tórax (normal, tonel/barril, chato, infundibuliforme/pectus excavatum, cariniforme/pectus carinatum, cifoescoliose). Simetria. Abaulamentos ou retrações localizadas. Cicatrizes cirúrgicas, drenos. Ginecomastia (em homens com DPOC ou câncer de pulmão). Circulação colateral.
  * **Inspeção Dinâmica:**
    * Tipo respiratório (costal superior, toracoabdominal, abdominal).
    * Amplitude dos movimentos respiratórios (simétrica, diminuída uni ou bilateralmente).
    * Ritmo respiratório.
    * Uso de musculatura acessória. Tiragem intercostal, supraclavicular, de fúrcula. Respiração paradoxal.
  * **Palpação:**
    * Sensibilidade (pontos dolorosos, fraturas de costela).
    * Estrutura da parede torácica (massas, crepitações – enfisema subcutâneo).
    * Expansibilidade torácica (ápices e bases – simetria).
    * Frêmito Tóraco-Vocal (FTV): Normal, aumentado (consolidação), diminuído/abolido (derrame pleural, pneumotórax, atelectasia, obesidade, enfisema).
  * **Percussão (comparativa e simétrica):**
    * Som claro pulmonar (normal).
    * Hiper-ressonância ou timpanismo (pneumotórax, enfisema, grandes bolhas).
    * Submacicez ou Macicez (consolidação pneumônica, atelectasia, derrame pleural, massa tumoral).
    * Percussão das linhas axilares e bases. Excursão diafragmática (manobra de Lemos Torres).
  * **Ausculta Pulmonar (em todas as regiões do tórax – anterior, lateral, posterior; ápices e bases; comparativa e simétrica):**
    * **Sons Respiratórios Normais:** Murmúrio Vesicular (MV) (predominante), Som Brônquico (traqueia, grandes brônquios – patológico se em outras áreas), Som Broncovesicular (regiões paraesternais e interescapulares). Qualidade e intensidade do MV (normal, diminuído, abolido, rude).
    * **Ruídos Adventícios (Anormais):**
      * **Descontínuos:** Estertores Finos ou Crepitantes (final da inspiração; não se alteram com tosse; pneumonia, fibrose, congestão). Estertores Grossos ou Bolhosos (inspiração e expiração; alteram-se com tosse; secreção em brônquios).
      * **Contínuos:** Sibilos (musicais, agudos; inspi e/ou expi; broncoespasmo – asma, DPOC, bronquite). Roncos (graves, semelhantes ao ressonar; inspi e/ou expi; secreção em vias aéreas maiores; alteram-se com tosse). Estridor (som alto, predominantemente inspiratório; obstrução de via aérea superior – laringe/traqueia).
    * **Atrito Pleural:** (Som rude, áspero, "roçar de couro"; inspi e expi; pleurite).
    * **Ausculta da Voz (Ressonância Vocal):** Broncofonia (aumento da nitidez da voz – consolidação), Pectorilóquia fônica (voz sussurrada bem audível – consolidação), Egofonia (voz anasalada, "de cabra", na borda superior de derrame pleural).
* **Exame Cardiovascular:**
  * Ictus cordis. Ausculta cardíaca (ritmo, bulhas B1/B2, presença de B3/B4, sopros). Componente P2 da segunda bulha (hiperfonético em hipertensão pulmonar).
* **Abdômen:** Hepatomegalia (sinal de cor pulmonale), ascite, refluxo hepatojugular.
* **Extremidades:** Edema de membros inferiores (ICC, cor pulmonale), cianose periférica. Pulsos periféricos.

## 12. Exames Complementares Trazidos pelo Paciente
* **Radiografia de Tórax (PA e perfil) – laudo e imagem.**
* **Tomografia Computadorizada (TC) de Tórax (alta resolução?) – laudo e imagem.**
* **Espirometria / Prova de Função Pulmonar completa (com broncodilatador, volumes pulmonares, capacidade de difusão – DLCO).**
* **Gasometria Arterial. Oximetria de pulso (incluindo teste de caminhada de 6 minutos, se realizado).**
* **Exames de Escarro (Baciloscopia, cultura para BK, cultura para germes comuns, citologia oncótica).**
* **Testes alérgicos cutâneos ou RAST/IgE específica.**
* **Polissonografia (resultado).**
* **Cintilografia pulmonar V/Q. Angio-TC de artérias pulmonares.**
* **Ecocardiograma (avaliar função cardíaca, sinais de hipertensão pulmonar).**
* **Biópsias (brônquica, transbrônquica, pulmonar, pleural, de linfonodo).**
* **Exames laboratoriais gerais (hemograma, PCR, VHS, função renal/hepática, etc.).**

## 13. Hipótese(s) Diagnóstica(s)
* Diagnóstico(s) principal(is) (ex: Asma não controlada, DPOC exacerbado, Pneumonia Comunitária, Câncer de Pulmão, Tuberculose Pulmonar, SAOS grave).
* Diagnósticos diferenciais.
* Avaliação da gravidade e controle da doença (se crônica).

## 14. Plano Terapêutico e Conduta
* **Tratamento Medicamentoso:**
  * Prescrição de inaladores (broncodilatadores de curta/longa ação, corticoides inalatórios, combinações), corticoides orais/sistêmicos, antibióticos, antivirais, antifúngicos, oxigenoterapia, etc. (Nome, dose, via, posologia, duração).
  * Instrução e checagem da técnica inalatória.
* **Tratamento Não Medicamentoso:**
  * Cessação do tabagismo (aconselhamento, farmacoterapia).
  * Reabilitação pulmonar. Fisioterapia respiratória.
  * Oxigenoterapia domiciliar prolongada (ODP) – indicação, prescrição.
  * CPAP/BiPAP para SAOS/Insuficiência Respiratória.
  * Vacinação.
  * Controle ambiental (remoção de alérgenos/irritantes).
  * Orientação nutricional.
* **Educação do Paciente:**
  * Sobre a doença (causas, sintomas, evolução, tratamento).
  * Uso correto das medicações. Plano de ação para crises/exacerbações.
  * Reconhecimento de sinais de alerta.
  * Importância da adesão ao tratamento e acompanhamento.
* **Solicitação de Exames Complementares Adicionais:** (Especificar e justificar – PFP, imagem, laboratoriais, broncoscopia, etc.).
* **Encaminhamentos:** Fisioterapeuta respiratório, nutricionista, psicólogo, cirurgião torácico, oncologista, infectologista, otorrinolaringologista, cardiologista.
* **Agendamento de Retorno:** Data/prazo para reavaliação, resultados de exames, ajuste de tratamento.
`,
  return: `Consulta de Retorno - Pneumologia

## 1. Lista de Problemas e HDA Prévia da Consulta Anterior
* **Diagnóstico(s) pneumológico(s) estabelecido(s).**
* **Plano terapêutico instituído (medicamentoso, não medicamentoso, oxigenoterapia, etc.).**
* **Metas terapêuticas (controle de sintomas, melhora da função pulmonar, redução de exacerbações).**

## S - Subjetivo
* **Motivo do Retorno:** (Agendado, reavaliação de tratamento, resultados de exames, piora do quadro, exacerbação).
* **Evolução dos Sintomas Respiratórios desde a Última Consulta:**
  * **Dispneia:** Melhora, piora, estável? (Comparar com mMRC ou atividades).
  * **Tosse:** Frequência, intensidade, produtividade (características do escarro).
  * **Sibilância:** Frequência, necessidade de medicação de resgate.
  * **Dor torácica.**
  * **Utilização de questionários de sintomas (ex: CAT, ACQ), se aplicável – comparar pontuação.**
* **Adesão ao Tratamento:**
  * **Medicações Inalatórias:** Usou conforme prescrito? (Nome, dose, horários). Técnica inalatória (rechecar). Dificuldades? Efeitos colaterais?
  * **Medicações Orais:** Tomou corretamente? Efeitos colaterais?
  * **Oxigenoterapia:** Usou conforme orientado (fluxo, horas/dia)? Problemas com o equipamento?
  * **CPAP/BiPAP:** Adaptação, horas de uso/noite, problemas com máscara/equipamento.
  * **Reabilitação Pulmonar/Fisioterapia:** Compareceu às sessões? Percebeu melhora?
* **Uso de Medicação de Resgate (Broncodilatador de Curta Ação):** Frequência (quantas vezes ao dia/semana).
* **Ocorrência de Exacerbações/Crises desde a Última Consulta:**
  * Quantas? Fatores desencadeantes?
  * Sintomas durante a exacerbação.
  * Procurou atendimento médico/emergência? Necessidade de antibióticos, corticoides orais, internação?
* **Tabagismo:** (Se fumante) Parou? Reduziu? Tentativas? (Se ex-fumante) Permanece abstinente?
* **Novos Sintomas ou Preocupações.**
* **Impacto na Qualidade de Vida e Atividades Diárias:** Comparar com avaliação anterior.
* **Estado Vacinal:** Alguma vacina administrada desde a última consulta?
* **Dúvidas do Paciente.**

## O - Objetivo
* **Sinais Vitais:** PA, FC, FR, Tax, SpO₂ (em ar ambiente e/ou com O₂ suplementar, se em uso). Comparar com valores anteriores.
* **Exame Físico Pulmonar e Geral Direcionado:**
  * Reavaliar achados da ausculta pulmonar (MV, sibilos, estertores).
  * Sinais de esforço respiratório, cianose.
  * Edema de membros inferiores, turgência jugular (sinais de cor pulmonale).
  * Comparar com o exame físico anterior.
* **Técnica Inalatória:** Pedir ao paciente para demonstrar o uso do(s) seu(s) dispositivo(s) inalatório(s). Corrigir se necessário.
* **Resultados de Exames Complementares Solicitados:**
  * Prova de Função Pulmonar (Espirometria, etc.) – comparar com anteriores (VEF1, CVF, relação VEF1/CVF, PFE).
  * Gasometria Arterial.
  * Radiografia/TC de Tórax.
  * Exames laboratoriais (hemograma, PCR, etc.).
  * Dados do CPAP (se disponíveis – índice de apneia-hipopneia residual, vazamento).

## A - Avaliação
* **Status Atual da(s) Doença(s) Pneumológica(s):**
  * Controle da doença (ex: asma controlada/parcialmente controlada/não controlada; DPOC estável/exacerbado – classificação GOLD).
  * Progressão ou estabilidade de doenças crônicas (fibrose, bronquiectasias).
* **Resposta à Terapia Instituída:**
  * Melhora sintomática, funcional (PFP), radiológica?
  * Atingimento das metas terapêuticas.
  * Se resposta insatisfatória, analisar causas (adesão, técnica inalatória inadequada, dose insuficiente, diagnóstico incorreto, progressão da doença, comorbidades não controladas, exposição contínua a gatilhos).
* **Avaliação de Risco:** Risco de exacerbações futuras, progressão da doença, mortalidade.
* **Tolerabilidade do Tratamento:** Efeitos colaterais, necessidade de ajuste.
* **Necessidade de Otimização Terapêutica.**

## P - Plano
* **Ajustes no Tratamento Medicamentoso:**
  * Manter, aumentar/diminuir doses, trocar medicações/dispositivos inalatórios, adicionar novas drogas (ex: "step-up" ou "step-down" na asma/DPOC).
  * Otimizar terapia para SAOS (ajuste de pressão do CPAP).
  * Considerar início ou ajuste de oxigenoterapia.
* **Reforço Educacional:**
  * Técnica inalatória.
  * Importância da adesão.
  * Plano de ação para exacerbações (uso de medicação de resgate, quando procurar médico).
  * Manejo de comorbidades.
  * Cessação do tabagismo (reforçar, oferecer ajuda).
* **Tratamento Não Medicamentoso:**
  * Encaminhar/reencaminhar para reabilitação pulmonar.
  * Orientar sobre atividade física, nutrição.
  * Vacinação (atualizar se necessário).
* **Solicitação de Novos Exames Complementares:** (Para monitoramento, avaliação de progressão, investigação de complicações).
* **Encaminhamentos:** (Manter ou novos).
* **Agendamento de Retorno:** Definir intervalo com base na gravidade, controle da doença e necessidade de monitoramento. Fornecer orientações para retorno antecipado se necessário.
`,
},

"Pediatria": {
  first: `Anamnese para Primeira Consulta - Pediatria

## 1. Identificação da Criança e do(s) Responsável(eis)
* **Nome completo da criança:**
* **Data de Nascimento:**
* **Idade (anos, meses e dias):**
* **Sexo:**
* **Nome completo da mãe:**
* **Nome completo do pai:**
* **Nome do(s) responsável(eis) presente(s) na consulta e grau de parentesco:**
* **Telefone(s) de contato:**
* **Endereço completo:**
* **Convênio/SUS:**
* **Quem indicou/Como chegou ao serviço:**
* **Escola/Creche que frequenta (se aplicável):**
* **Data da Consulta:**

## 2. Queixa Principal (QP)
* **Motivo principal da consulta (registrar com as palavras do acompanhante ou da criança, se esta tiver idade para expressar, e a duração):**
  * Ex: "Febre e tosse há 3 dias", "Não está ganhando peso", "Consulta de rotina (puericultura)".

## 3. História da Doença Atual (HDA) (ou motivo da consulta de puericultura)
* **Se Doença Aguda:**
  * **Início dos sintomas:** Quando e como começou?
  * **Características dos sintomas:**
    * **Febre:** Aferida? Valor máximo? Duração? Uso de antitérmicos (qual, dose, resposta)? Calafrios?
    * **Tosse:** Seca ou produtiva? Cor e aspecto da secreção? Período do dia em que piora? Dispneia associada? Chiado?
    * **Vômitos:** Frequência, quantidade, aspecto (alimentar, bilioso, aquoso), relação com alimentação, jato?
    * **Diarreia:** Frequência, consistência (líquida, pastosa), cor, presença de muco ou sangue, odor fétido?
    * **Dor:** Localização, tipo, intensidade, irradiação, fatores de melhora/piora.
    * **Lesões de pele:** Tipo, localização, prurido, evolução.
    * **Outros sintomas específicos.**
  * **Sintomas associados:** Irritabilidade, prostração, recusa alimentar, choro fácil, alterações no sono.
  * **Evolução do quadro:** Melhora, piora, estacionário.
  * **Medicações utilizadas em casa:** Nome, dose, frequência, quem prescreveu, resposta.
  * **Procurou outro serviço de saúde? Qual conduta?**
  * **Contato com pessoas doentes.**
* **Se Acompanhamento de Doença Crônica:**
  * Resumo da doença de base.
  * Sintomas atuais relacionados à doença.
  * Controle da doença, adesão ao tratamento.
  * Intercorrências desde a última consulta com especialista.
* **Se Puericultura (Consulta de Rotina):**
  * Objetivo da consulta (avaliação de crescimento e desenvolvimento, orientações, vacinação).
  * Dúvidas específicas dos pais/responsáveis.
  * Como a criança tem passado desde a última consulta de puericultura (se houver).

## 4. Antecedentes Perinatais (Gestacionais e Neonatais)
* **Mãe:** Idade, paridade (G P A C), escolaridade, profissão. Planejou a gravidez?
* **Pré-Natal:**
  * Início e número de consultas. Fez todos os exames?
  * Intercorrências na gestação: Infecções (sífilis, toxoplasmose, rubéola, citomegalovírus, HIV, Zika, outras), diabetes gestacional, hipertensão arterial (DHEG, pré-eclâmpsia), sangramentos, uso de álcool, tabaco ou outras drogas.
  * Medicações utilizadas na gestação. Suplementação de ácido fólico e ferro.
  * Sorologias maternas (VDRL, HIV, HBsAg, Anti-HCV, Toxoplasmose IgG/IgM, Rubéola IgG/IgM, etc.). Tipagem sanguínea e fator Rh da mãe.
* **Parto:**
  * Idade gestacional ao nascer (semanas).
  * Tipo de parto: Vaginal (normal), fórceps, cesárea (indicação da cesárea).
  * Local do parto (hospital, domicílio).
  * Apresentação fetal. Duração do trabalho de parto.
  * Intercorrências no parto (sofrimento fetal, mecônio, febre materna).
* **Período Neonatal (primeiros 28 dias):**
  * Peso ao nascer (g), Comprimento (cm), Perímetro Cefálico (cm).
  * Boletim de Apgar (1º e 5º minuto).
  * Necessidade de reanimação neonatal (oxigênio, VPP, intubação, massagem cardíaca)?
  * Internação em UTI neonatal? Motivo, tempo de permanência.
  * Icterícia neonatal: Início, duração, tratamento (fototerapia, exsanguineotransfusão).
  * Hipotermia, hipoglicemia, desconforto respiratório, infecções neonatais.
  * Eliminou mecônio e urina nas primeiras 24-48h?
  * Alta hospitalar com a mãe?
* **Triagem Neonatal:**
  * **Teste do Pezinho:** Realizado? Resultado? (Se alterado, qual doença e acompanhamento).
  * **Teste da Orelhinha (Emissões Otoacústicas):** Realizado? Resultado?
  * **Teste do Olhinho (Reflexo Vermelho):** Realizado? Resultado?
  * **Teste do Coraçãozinho (Oximetria de Pulso):** Realizado? Resultado?
  * **Teste da Linguinha:** Realizado? Resultado?
* **Administração de Vitamina K e Credé (nitrato de prata ocular).**

## 5. Alimentação
* **Aleitamento Materno:**
  * Início (na primeira hora de vida?).
  * Exclusivo (AME) até que idade? Se não, por quê?
  * Dificuldades na amamentação (dor, fissuras, ingurgitamento, pega incorreta, percepção de "leite fraco/pouco leite").
  * Frequência e duração das mamadas (livre demanda?).
  * Uso de mamadeiras, chupetas, bicos de silicone.
* **Fórmula Infantil (se utilizou/utiliza):**
  * Motivo da introdução. Qual fórmula? Quem indicou?
  * Diluição (como prepara). Volume e frequência. Aceitação.
* **Introdução Alimentar (a partir dos 6 meses):**
  * Idade de início. Como foi feita?
  * Alimentos oferecidos (frutas, legumes, verduras, cereais, tubérculos, carnes, leguminosas).
  * Aceitação de diferentes texturas e sabores.
  * Quem oferece a alimentação? Ambiente das refeições.
  * Uso de sal, açúcar, alimentos industrializados/ultraprocessados.
* **Alimentação Atual (conforme a idade):**
  * Recordatório alimentar das últimas 24 horas ou de um dia típico.
  * Número de refeições/dia (café da manhã, lanche, almoço, lanche, jantar, ceia). Horários.
  * Variedade e qualidade dos alimentos. Porções.
  * Consumo de água. Sucos (naturais, industrializados). Refrigerantes.
  * Apetite (bom, diminuído, seletivo). Preferências e recusas alimentares.
  * A criança come sozinha? Usa talheres?
* **Suplementação de Vitaminas e Minerais:** Vitamina D (até 2 anos), Ferro (profilático a partir de qual idade e dose, ou terapêutico). Outras.

## 6. Crescimento e Desenvolvimento Neuropsicomotor (DNPM)
* **Crescimento:**
  * Percepção dos pais sobre o crescimento da criança (está crescendo bem?).
  * Trazer carteira da criança com registros anteriores de peso, altura e perímetro cefálico para avaliação das curvas de crescimento.
* **Desenvolvimento (principais marcos para a idade – perguntar quando realizou):**
  * **Motor Grosseiro:** Sustentou a cabeça, rolou, sentou sem apoio, engatinhou, ficou em pé com apoio, andou com apoio, andou sem apoio, correu, subiu escadas, pulou.
  * **Motor Fino:** Segurou objetos, transferiu objetos de uma mão para outra, pinça (polegar-indicador), rabiscou, desenhou círculo, torre de cubos.
  * **Linguagem:** Sorriso social, balbucio, primeiras palavras (com significado), combinação de palavras, frases completas, compreende ordens.
  * **Social/Afetivo:** Interage com os pais/cuidadores, estranha pessoas, brinca (tipo de brincadeira), demonstra afeto, compartilha.
  * **Cognitivo:** Interesse por objetos, explora o ambiente, imita gestos, reconhece partes do corpo.
* **Controle Esfincteriano:** Idade do desfralde (diurno e noturno – vesical e anal). Enurese? Encoprese?
* **Escolaridade:** Idade que iniciou creche/escola. Adaptação. Rendimento escolar. Dificuldades de aprendizado ou comportamento na escola.
* **Há alguma preocupação dos pais em relação ao desenvolvimento da criança?**

## 7. Imunização
* **Carteira de vacinação está em dia? (SOLICITAR E CONFERIR A CARTEIRA).**
* **Vacinas recebidas (básicas do PNI e complementares, se houver). Doses e datas.**
* **Ocorrência de reações adversas importantes às vacinas.**
* **Vacinas em atraso? Motivo.**

## 8. Antecedentes Patológicos Pregressos (Doenças Anteriores e Internações)
* **Doenças comuns da infância já apresentadas (catapora, caxumba, rubéola, sarampo, coqueluche, viroses exantemáticas, "doenças de garganta", otites, pneumonias, diarreias, infecção urinária). Frequência e gravidade.**
* **Doenças crônicas diagnosticadas (asma, rinite alérgica, dermatite atópica, refluxo gastroesofágico, constipação crônica, epilepsia, cardiopatia congênita, etc.). Acompanhamento? Medicações?**
* **Alergias:**
  * Alimentares (leite de vaca, ovo, trigo, soja, amendoim, etc. – tipo de reação).
  * Medicamentosas (quais? tipo de reação).
  * Picadas de inseto, ambientais (poeira, ácaros, mofo, pelos de animais).
* **Cirurgias prévias:** Motivo, data, intercorrências.
* **Internações hospitalares:** Motivo, data, duração, tratamento realizado.
* **Traumatismos importantes (quedas, fraturas, TCE). Acidentes domésticos.**
* **Uso prévio prolongado de medicações (corticoides, antibióticos).**

## 9. Hábitos
* **Sono:**
  * Onde dorme (cama própria, quarto dos pais, berço)? Posição de dormir (supina – barriga para cima).
  * Rotina para dormir. Horário que dorme e acorda.
  * Número de horas de sono por noite. Cochilos durante o dia (frequência, duração).
  * Qualidade do sono: Agitado, despertares noturnos frequentes, terror noturno, sonambulismo, roncos, apneia observada.
* **Eliminações:**
  * **Urinárias:** Frequência, jato forte, dor/ardor ao urinar, escape de urina.
  * **Intestinais:** Frequência, consistência e aspecto das fezes, dor ou esforço para evacuar, escape de fezes.
* **Atividade Física e Brincadeiras:**
  * Tempo dedicado a atividades físicas e brincadeiras ativas. Tipo de atividade.
  * Brinca ao ar livre?
* **Tempo de Tela:**
  * Horas por dia (TV, computador, tablet, celular). Conteúdo assistido.
* **Hábitos Orais:** Uso de chupeta, sucção digital, roer unhas, bruxismo.
* **Higiene:** Banhos (frequência), higiene oral (escovação dentária – frequência, com ou sem creme dental com flúor, ajuda dos pais), lavagem das mãos.

## 10. Condições Socioambientais e Familiares
* **Composição Familiar:** Quem mora com a criança? Idade e ocupação dos pais/cuidadores.
* **Dinâmica Familiar:** Relacionamento entre os membros da família. Eventos estressantes recentes (separação, luto, mudança).
* **Saúde dos Pais e Irmãos:** Doenças crônicas, mentais, infecciosas.
* **Consanguinidade entre os pais.**
* **Moradia:** Tipo (casa, apartamento), número de cômodos, ventilação, exposição ao sol.
* **Saneamento Básico:** Água tratada, esgoto, coleta de lixo.
* **Animais de Estimação:** Tipo, vacinação do animal, contato da criança.
* **Exposição à Fumaça de Cigarro (Tabagismo Passivo):** Alguém fuma em casa ou perto da criança?
* **Condições de Trabalho dos Pais:** (Pode influenciar tempo com a criança, estresse, exposição a riscos).
* **Apoio Social e Familiar:** Rede de apoio para cuidados com a criança.
* **Situação Socioeconômica:** (Avaliar de forma sensível, se relevante para acesso a recursos, alimentação, etc.).
* **Segurança:** Uso de cinto de segurança/cadeirinha no carro, grades em janelas, proteção em tomadas, armazenamento seguro de medicamentos e produtos de limpeza. Prevenção de afogamentos.

## 11. Revisão de Sistemas (Perguntar sobre sintomas recentes, direcionado pela idade e queixa)
* **Geral:** Febre, irritabilidade, choro excessivo, prostração, hipo/hiperatividade, alterações no ganho de peso.
* **Pele e Anexos:** Erupções cutâneas, palidez, icterícia, cianose, equimoses, petéquias, prurido, queda de cabelo, alterações ungueais.
* **Cabeça:** Cefaleia, tonturas, traumatismos. Fontanela (tensa, deprimida, pulsátil – em lactentes).
* **Olhos:** Secreção, hiperemia, lacrimejamento, estrabismo, acompanha objetos, queixas visuais.
* **Ouvidos:** Dor, secreção, audição (responde a sons?).
* **Nariz:** Coriza (aspecto), obstrução nasal, espirros, roncos, respiração bucal, epistaxe.
* **Boca e Garganta:** Dor de garganta, dificuldade para engolir, salivação excessiva, lesões orais, sangramento gengival. Dentição (erupção dos dentes).
* **Pescoço:** Linfonodos palpáveis, torcicolo, dor à movimentação.
* **Respiratório:** Tosse (características), chiado, cansaço, dificuldade para respirar (tiragem, batimento de asa de nariz), apneia.
* **Cardiovascular:** Cianose (labial, extremidades), cansaço durante as mamadas/atividades, sudorese excessiva na cabeça durante mamadas, palpitações, sopros cardíacos conhecidos.
* **Gastrointestinal:** Apetite, náuseas, vômitos, regurgitação, diarreia, constipação, dor abdominal (localização, tipo, relação com alimentos), distensão abdominal, sangue ou muco nas fezes.
* **Geniturinário:** Jato urinário (meninos), frequência das micções, disúria, enurese, corrimento vaginal, alterações na genitália externa, puberdade (sinais precoces ou tardios).
* **Músculo-esquelético e Extremidades:** Dores em membros, claudicação (mancar), edema ou deformidades articulares, fraqueza muscular, alterações de marcha.
* **Neurológico:** Convulsões, desmaios, alterações de comportamento, tiques, sonolência excessiva, atraso no desenvolvimento.
* **Emocional/Comportamental:** Humor habitual, medos, ansiedades, agressividade, isolamento, dificuldades de relacionamento.

## 12. Exame Físico Pediátrico Completo (Realizado de forma lúdica e adaptada à idade)
* **Observação Geral:**
  * Estado geral: Ativo, reativo, alerta, sonolento, irritadiço, prostrado, toxemiado.
  * Nível de consciência e interação com o ambiente/examinador.
  * Estado nutricional (eutrófico, desnutrido, sobrepeso, obeso).
  * Estado de hidratação (mucosas úmidas/secas, turgor da pele, enchimento capilar).
  * Fácies (atípica, sindrômica?). Padrão respiratório. Presença de malformações visíveis.
* **Dados Antropométricos:**
  * Peso (balança calibrada, criança despida ou com o mínimo de roupa).
  * Altura/Comprimento (estadiômetro/antropômetro adequado).
  * Perímetro Cefálico (PC) – até 2-3 anos de idade.
  * (Plotar todos os dados nas curvas de crescimento da OMS ou específicas do país/região – P/I, A/I, P/A, IMC/I, PC/I).
  * Índice de Massa Corporal (IMC) para crianças maiores de 2 anos.
* **Sinais Vitais:**
  * Frequência Cardíaca (FC) – auscultar por 1 minuto.
  * Frequência Respiratória (FR) – observar por 1 minuto.
  * Temperatura Axilar (Tax).
  * Pressão Arterial (PA) – com manguito de tamanho adequado, se indicado pela idade (>3 anos rotina, ou antes se condições específicas).
  * Saturação de Oxigênio (SpO₂) – se desconforto respiratório ou condição que afete oxigenação.
* **Pele e Fâneros:**
  * Cor (rósea, pálida, ictérica, cianótica). Umidade, textura, elasticidade, turgor.
  * Lesões dermatológicas: Descrever tipo (mácula, pápula, vesícula, etc.), localização, distribuição, aspecto. Nevos, hemangiomas.
  * Cabelos: Implantação, distribuição, brilho, textura, fragilidade. Couro cabeludo.
  * Unhas: Formato, cor, lesões.
  * Presença de linfonodos palpáveis (descrever localização, tamanho, consistência, mobilidade, sensibilidade).
* **Cabeça:**
  * Formato, simetria. Circunferência (comparar com curva).
  * Fontanelas (em lactentes): Anterior (tamanho, normotensa, abaulada, deprimida, pulsátil), Posterior (geralmente fecha antes).
  * Suturas cranianas (abertas, cavalgadas). Craniotabes.
  * Couro cabeludo: lesões, hematomas.
* **Olhos:**
  * Simetria, alinhamento. Pálpebras (edema, ptose).
  * Conjuntivas (pálidas, hiperemiadas). Escleras (cor).
  * Pupilas (tamanho, forma, fotorreação direta e consensual).
  * Reflexo vermelho (pesquisar em ambiente escurecido).
  * Motilidade ocular extrínseca (acompanha objeto). Estrabismo (teste de cobertura).
  * Secreção ocular. Lacrimejamento.
* **Ouvidos:**
  * Implantação e formato do pavilhão auricular. Presença de apêndices pré-auriculares, fístulas.
  * Otoscopia: Conduto auditivo externo (secreção, cerume, corpo estranho, hiperemia). Membrana timpânica (cor – perolácea, opaca, hiperemiada; brilho; transparência; mobilidade à otoscopia pneumática, se disponível; abaulamento ou retração; perfuração).
* **Nariz:**
  * Forma externa, permeabilidade das narinas (observar respiração).
  * Rinoscopia anterior (com otoscópio): Mucosa nasal (cor, umidade, edema), septo nasal (desvio), presença de secreção (aspecto), cornetos (hipertrofia).
* **Boca e Orofaringe:**
  * Lábios (cor, hidratação, fissuras, lesões).
  * Mucosa oral (cor, umidade, aftas, lesões). Gengivas.
  * Dentes: Número, erupção (conforme idade), cáries, higiene.
  * Língua: Aspecto (saburra, lisa, geográfica), frênulo lingual.
  * Palato duro e mole. Úvula (centrada, bífida?).
  * Orofaringe: Pilares amigdalianos, amígdalas (tamanho – graduação, hiperemia, exsudato, criptas), parede posterior da faringe.
* **Pescoço:**
  * Mobilidade (ativa e passiva). Simetria.
  * Palpação de cadeias linfonodais (cervical anterior, posterior, submandibular, submentoniana, supraclavicular).
  * Tireoide (geralmente não palpável em crianças pequenas, a menos que aumentada).
  * Presença de massas, fístulas, torcicolo. Turgência jugular (raro em crianças, mas observar).
* **Tórax e Pulmões:**
  * Inspeção: Forma (globoso em lactentes, elíptico em maiores), simetria, deformidades (pectus excavatum/carinatum). Padrão respiratório (abdominal em lactentes, toracoabdominal em maiores), frequência, ritmo, profundidade. Presença de retrações (intercostal, subcostal, supraclavicular, fúrcula), batimento de asa de nariz (BAN), tiragem.
  * Palpação: Expansibilidade torácica, frêmito tóraco-vocal (em crianças colaborativas).
  * Percussão: Som claro pulmonar (em crianças colaborativas).
  * Ausculta Pulmonar: Murmúrio vesicular (presente, diminuído, abolido, simétrico). Ruídos adventícios (estertores finos/grossos, sibilos, roncos, atrito pleural, estridor). Transmissão da voz (em crianças colaborativas).
* **Coração:**
  * Inspeção e Palpação: Abaulamentos precordiais. Ictus cordis (localização – linha hemiclavicular, espaço intercostal; intensidade; extensão). Frêmitos.
  * Ausculta Cardíaca: Frequência e ritmo cardíaco. Bulhas cardíacas (B1 e B2 – fonética, desdobramentos fisiológicos/patológicos). Bulhas acessórias (B3, B4, estalidos). Sopros cardíacos (localização – foco; irradiação; tempo na sístole/diástole; intensidade – cruzes +/6+; timbre/qualidade; relação com posição/respiração). Atrito pericárdico.
  * Pulsos periféricos (braquial, radial, femoral, pedioso, tibial posterior – simetria, amplitude, frequência). Enchimento capilar (<2-3s).
* **Abdômen:**
  * Inspeção: Forma (plano, globoso, escavado, distendido, batráquio), simetria, cicatriz umbilical (hérnia, onfalite em RN), movimentos respiratórios, ondas peristálticas visíveis, circulação colateral.
  * Ausculta: Ruídos hidroaéreos (presentes, aumentados, diminuídos, ausentes – timbre metálico?). Sopros abdominais.
  * Percussão: Timpanismo predominante. Macicez hepática (hepatimetria percutida), esplênica (espaço de Traube). Macicez em bexiga cheia ou massas. Sinal do piparote (ascite).
  * Palpação:
    * Superficial: Sensibilidade, resistência da parede, massas superficiais.
    * Profunda: Órgãos (fígado – borda, consistência, superfície, dor; baço – normalmente não palpável ou ponta em lactentes; rins – em RN e lactentes delgados), massas (localização, tamanho, forma, consistência, mobilidade, sensibilidade), dor à descompressão brusca (DB+).
    * Hérnias (inguinal, umbilical – redutibilidade).
* **Genitália:**
  * **Meninos:** Pênis (tamanho, fimose fisiológica/patológica, hipospádia, epispádia). Meato uretral. Escroto (simetria, edema, lesões). Testículos (presentes na bolsa escrotal, tamanho, consistência, sensibilidade). Hérnia inguino-escrotal, hidrocele.
  * **Meninas:** Vulva (grandes e pequenos lábios, clitóris, hímen – aspecto conforme idade). Secreção vaginal fisiológica ou patológica. Sinais de sinéquia de pequenos lábios, inflamação, trauma.
  * **Puberdade (Escala de Tanner):** Avaliar desenvolvimento de pelos pubianos (P1-P5), desenvolvimento mamário em meninas (M1-M5), desenvolvimento genital em meninos (G1-G5).
* **Ânus e Região Perianal:** Inspeção (fissuras, plicomas, dermatite, fístulas, prolapso retal). Tônus do esfíncter (observacional). (Toque retal apenas se estritamente indicado).
* **Membros e Coluna Vertebral:**
  * **Membros:** Simetria, comprimento, deformidades congênitas ou adquiridas, edema, cianose, temperatura. Articulações (edema, rubor, calor, dor, limitação de movimento). Força muscular (adaptado à idade).
  * **Quadril (lactentes):** Manobras de Ortolani e Barlow (até 3-6 meses). Limitação da abdução. Assimetria de pregas cutâneas. Sinal de Galeazzi.
  * **Pés:** Formato, arco plantar, hálux valgo, pé torto.
  * **Coluna Vertebral:** Inspeção (alinhamento – escoliose, cifose, lordose). Palpação (sensibilidade, contraturas). Manobra de Adams (pesquisa de escoliose em escolares/adolescentes). Região sacrococcígea (fosseta pilonidal, tufo de pelos).
* **Exame Neurológico (adaptado à idade e cooperação):**
  * **Lactentes:** Nível de alerta, contato visual, sorriso social, choro (características). Tônus muscular (ativo e passivo – hipotonia/hipertonia). Reflexos primitivos (Moro, sucção, preensão palmar/plantar, cutâneo-plantar em extensão – Babinski, marcha reflexa, tônico-cervical assimétrico – RTCA). Movimentação espontânea dos membros. Perímetro cefálico.
  * **Pré-escolares e Escolares:** Nível de consciência, orientação (tempo, espaço, pessoa – se maior). Linguagem (compreensão e expressão). Marcha, equilíbrio (andar em linha reta, ponta dos pés, calcanhares), coordenação (index-nariz, movimentos alternados rápidos). Força muscular global. Reflexos tendinosos profundos (bicipital, tricipital, patelar, aquileu). Sensibilidade (tátil, dolorosa – se queixa específica e colaborativo). Pares cranianos (observação da mímica facial, movimentos oculares, audição, deglutição).
  * **Sinais de Irritação Meníngea:** Rigidez de nuca, Kernig, Brudzinski (se suspeita clínica).

## 13. Avaliação do Desenvolvimento Global
* **Com base nos marcos, observação e, se necessário, aplicação de escalas de triagem (ex: Teste de Denver II, ASQ – Ages and Stages Questionnaires).**
* **Correlacionar com a idade cronológica. Identificar áreas de atraso ou preocupação.**

## 14. Exames Complementares Trazidos (se houver)
* **Analisar resultados de exames laboratoriais, de imagem, carteira de vacinação, teste do pezinho, etc.**

## 15. Diagnósticos Segmentados
* **a. Diagnóstico Antropométrico:**
  * (Avaliar curvas de Peso/Idade, Altura/Idade, IMC/Idade, Perímetro Cefálico/Idade. Classificar como eutrófico, risco de sobrepeso, sobrepeso, obesidade, obesidade grave, magreza, magreza acentuada, etc., conforme os pontos de corte e curvas de referência utilizadas - ex: OMS).
  * Ex: Eutrófico segundo P/I e A/I. Risco de sobrepeso segundo IMC/I.
* **b. Diagnóstico Nutricional/Alimentar:**
  * (Avaliar a qualidade e quantidade da alimentação, presença de deficiências ou excessos, adequação da introdução alimentar, aleitamento materno, uso de fórmulas, seletividade, etc.).
  * Ex: Aleitamento materno exclusivo adequado para a idade. Introdução alimentar complementar com boa aceitação e variedade. Risco para deficiência de ferro devido à baixa ingestão de alimentos fontes.
* **c. Diagnóstico Vacinal:**
  * (Avaliar a carteira de vacinação, verificar se está em dia para a idade, se há vacinas em atraso ou faltantes conforme o PNI e recomendações adicionais).
  * Ex: Calendário vacinal em dia para a idade. / Atraso na vacina X, orientada atualização.
* **d. Diagnóstico do Desenvolvimento Neuropsicomotor (DNPM):**
  * (Avaliar os marcos do desenvolvimento nas áreas motora grossa, motora fina, linguagem, social/afetivo e cognitivo. Identificar se estão adequados para a idade, se há sinais de alerta ou atrasos).
  * Ex: DNPM adequado para a idade em todas as áreas avaliadas. / Sinais de alerta para atraso na linguagem, encaminhado para avaliação fonoaudiológica.
* **e. Diagnóstico de Saúde Bucal:**
  * (Avaliar a condição dos dentes, gengivas, presença de cáries, hábitos de higiene oral, uso de flúor, encaminhamento ao odontopediatra).
  * Ex: Hígido oral, sem cáries visíveis. Orientada escovação supervisionada com creme dental fluoretado. / Presença de cáries incipientes, encaminhado ao odontopediatra.
* **f. Diagnóstico Patológico (Doenças Atuais e Pregressas):**
  * (Listar diagnósticos de condições agudas ou crônicas identificadas, ativas ou resolvidas. Incluir alergias, condições de base, etc.).
  * Ex: IVAS provavelmente viral. Dermatite atópica leve controlada. Sem outras patologias ativas. Alergia à proteína do leite de vaca em acompanhamento.

## 16. Plano e Conduta
* **Diagnóstico:** Se possível, definir.
* **Tratamento Medicamentoso (se necessário):** Nome da droga, dose (calcular por peso – mg/kg/dose ou mg/kg/dia), via de administração, posologia (intervalo e duração), orientações sobre preparo e administração.
* **Orientações e Aconselhamento (Puericultura):**
  * Alimentação (adequada para a idade, prevenção de deficiências e obesidade).
  * Sono (higiene do sono).
  * Higiene (oral, corporal).
  * Atividade física e brincadeiras (estímulo ao desenvolvimento).
  * Uso saudável de telas.
  * Prevenção de acidentes (domésticos, trânsito – conforme a faixa etária).
  * Desenvolvimento neuropsicomotor (sinais de alerta, como estimular).
  * Imunizações (calendário, importância, esclarecer dúvidas).
  * Saúde bucal (primeira consulta ao dentista).
  * Disciplina positiva, limites.
  * Sinais de alerta para procurar atendimento médico.
* **Solicitação de Exames Complementares:** (Se necessário para diagnóstico ou acompanhamento – justificar).
* **Encaminhamentos:** Para especialistas (fonoaudiólogo, fisioterapeuta, psicólogo, neuropediatra, cardiopediatra, etc.), se indicado.
* **Agendamento de Retorno:**
  * **Puericultura:** Conforme calendário recomendado (mensal no primeiro ano, trimestral no segundo, semestral/anual depois).
  * **Doença aguda:** Para reavaliação em X dias, ou se não houver melhora/piora.
  * **Doença crônica:** Conforme necessidade de monitoramento.
`,
  return: `Consulta de Retorno - Pediatria

## 1. Lista de Problemas e HDA Prévia da Consulta Anterior
* Resumo dos diagnósticos, queixas ou acompanhamentos da última consulta.
* Plano terapêutico e orientações fornecidas anteriormente.

## S - Subjetivo (Relatado pelo acompanhante/criança)
* **Motivo do Retorno:** (Agendado para puericultura, reavaliação de quadro agudo, acompanhamento de doença crônica, mostrar resultados de exames).
* **Evolução da Queixa da Consulta Anterior (se doença aguda/crônica):**
  * Houve melhora, piora ou resolução dos sintomas? Descrever.
  * A criança está mais ativa, brincando, se alimentando melhor?
* **Adesão às Orientações e Tratamento Prescrito:**
  * **Medicações:** Usou corretamente (dose, horários, duração)? Efeitos colaterais? Dificuldades na administração?
  * **Orientações (alimentares, comportamentais, etc.):** Conseguiu seguir? Dificuldades?
* **Surgimento de Novos Sintomas ou Preocupações desde a Última Consulta.**
* **Intervalo desde a Última Consulta:**
  * Como a criança passou nesse período? Alguma intercorrência (febre, vômito, diarreia, internação, visita à emergência, uso de outras medicações)?
  * Atualização sobre alimentação, sono, eliminações, atividades, desenvolvimento, desempenho escolar.
* **Dúvidas dos Pais/Responsáveis.**

## O - Objetivo
* **Dados Antropométricos:**
  * Peso, Altura/Comprimento, Perímetro Cefálico (se < 2-3 anos).
  * (Plotar nas curvas de crescimento e AVALIAR O INTERVALO DE CRESCIMENTO/VELOCIDADE desde a última medida).
  * Cálculo do IMC para > 2 anos.
* **Sinais Vitais:** FC, FR, Tax (PA se indicado).
* **Exame Físico Direcionado:**
  * Foco no motivo do retorno e na(s) condição(ões) em acompanhamento.
  * Comparar com o exame físico anterior, se relevante.
  * Para consultas de puericultura, realizar exame físico completo ou segmentar por consulta, garantindo a avaliação integral ao longo do tempo.
* **Resultados de Exames Solicitados na Consulta Anterior (se houver): Analisar.**
* **Revisão da Carteira de Vacinação:** Verificar se há vacinas em atraso ou próximas do calendário.
* **Avaliação do Desenvolvimento (observacional e/ou com instrumentos de triagem, conforme a idade e necessidade – ex: ASQ, marcos da caderneta).**

## A - Avaliação
* **Status do(s) Problema(s) Anterior(es):** Resolvido, em melhora, estável, piorou.
* **Resposta ao Tratamento (se aplicável).**
* **Avaliação do Crescimento:** Adequado para a idade? Ganho pondero-estatural satisfatório no período? Algum sinal de alerta nas curvas?
* **Avaliação do Desenvolvimento Neuropsicomotor:** Adequado para a idade? Algum atraso ou preocupação identificado?
* **Estado Nutricional.**
* **Estado Vacinal.**
* **Identificação de Novos Problemas ou Riscos.**
* **Avaliação Geral da Saúde e Bem-estar da Criança.**

## P - Plano
* **Manejo de Problemas Atuais (agudos ou crônicos):**
  * Manter, ajustar ou suspender medicações.
  * Novas prescrições, se necessário.
* **Orientações e Aconselhamento de Puericultura (antecipatórias para a próxima fase):**
  * Reforçar ou introduzir novas orientações sobre alimentação, sono, desenvolvimento, segurança, higiene, comportamento, etc.
* **Vacinação:** Orientar e/ou administrar vacinas devidas.
* **Rastreamento e Testes Preventivos:** (Conforme diretrizes para a idade – ex: solicitar hemograma para rastreio de anemia aos 12 meses, avaliação oftalmológica/auditiva).
* **Solicitação de Novos Exames (se necessário).**
* **Encaminhamentos (manter ou novos).**
* **Agendamento do Próximo Retorno:** (Puericultura, acompanhamento específico). Fornecer informações sobre sinais de alerta que motivem retorno antecipado.
`,
},

};
